import React, { useState, useEffect, useRef, useMemo } from 'react';
import { RefreshCw } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBookOpen,
  faCircleCheck,
  faHouseChimney,
  faPenToSquare,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import EmptyState from '../components/EmptyState';
import Modal from '../components/Modal';
import ProgressBar from '../components/ProgressBar';
import { coursesService, api, videosService, audiosService, API_ORIGIN, categoriesService } from '../services/api';
import { Course, Video, Audio, ContentCategory, CourseDiscountType } from '../types';
import { truncateWords } from '../utils/text';
import CourseAboutPageFields, { CharacteristicRow } from '../components/courses/CourseAboutPageFields';
import CourseCurriculumTab from '../components/courses/CourseCurriculumTab';
import CourseLessonUpdatesTab from '../components/courses/CourseLessonUpdatesTab';
import CoursePracticeSessionsTab from '../components/courses/CoursePracticeSessionsTab';
import CourseFaqsTab from '../components/courses/CourseFaqsTab';
import CourseSatisfactionTab from '../components/courses/CourseSatisfactionTab';
import CoursePricingAdminBlock from '../components/courses/CoursePricingAdminBlock';
import { getPublicCourseDetailUrl } from '../utils/publicSiteUrl';
import SiteBrandedLoader, { BrandSpinnerRing } from '../components/SiteBrandedLoader';
import {
  CoreSection,
  CoreFormRibbon,
  coreFieldClasses,
  CourseCoreQuickSpecFields,
  CourseCorePublishToggles,
  CourseCoreFreeCourseToggle,
  CourseCoreHomepageCarouselToggles,
} from '../components/courses/CourseCoreFormLayout';
import { COURSE_THUMBNAIL_HINT_FA } from '../constants/frontendImageHints';

// Audio Player Component
const AudioPlayerComponent: React.FC<{ audioUrl: string }> = ({ audioUrl }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => {
      if (audio.duration && isFinite(audio.duration)) {
        setDuration(audio.duration);
      }
    };
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('durationchange', updateDuration);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('durationchange', updateDuration);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, [audioUrl]);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;
    
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch(console.error);
    }
  };

  const handleSeek = (time: number) => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = time;
      setCurrentTime(time);
    }
  };

  const formatTime = (seconds: number): string => {
    if (isNaN(seconds) || !isFinite(seconds)) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="bg-white border border-purple-200 rounded-lg p-3">
      <audio ref={audioRef} src={audioUrl} preload="metadata" crossOrigin="anonymous" className="hidden" />
      
      {/* Progress Bar */}
      <div className="mb-2">
        <input
          type="range"
          min="0"
          max={duration || 0}
          step="0.1"
          value={currentTime}
          onChange={(e) => handleSeek(parseFloat(e.target.value))}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          style={{
            background: `linear-gradient(to right, rgb(147, 51, 234) 0%, rgb(147, 51, 234) ${progress}%, rgba(229, 231, 235, 1) ${progress}%, rgba(229, 231, 235, 1) 100%)`,
          }}
        />
      </div>
      
      {/* Controls */}
      <div className="flex items-center justify-between">
        <button
          onClick={togglePlayPause}
          className="w-8 h-8 rounded-full bg-purple-600 hover:bg-purple-700 text-white flex items-center justify-center transition-colors"
          aria-label={isPlaying ? 'توقف' : 'پخش'}
        >
          {isPlaying ? (
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
            </svg>
          ) : (
            <svg className="w-4 h-4 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>
        <span className="text-xs text-gray-600 font-mono">
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>
      </div>
      
      <style>{`
        input[type="range"]::-webkit-slider-thumb {
          appearance: none;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: rgb(147, 51, 234);
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
        
        input[type="range"]::-moz-range-thumb {
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: rgb(147, 51, 234);
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
      `}</style>
    </div>
  );
};

const MAX_EPISODES_PER_TYPE = 50;
const ENROLLMENTS_PAGE_SIZE = 20;

const courseOverviewCards = [
  {
    key: 'total',
    label: 'کل دوره‌ها',
    cardClassName: 'border-blue-100 from-blue-50 to-white',
    labelClassName: 'text-blue-700',
    valueClassName: 'text-blue-900',
    iconWrapperClassName: 'bg-blue-100 text-blue-700',
    icon: faBookOpen,
  },
  {
    key: 'published',
    label: 'منتشر شده',
    cardClassName: 'border-emerald-100 from-emerald-50 to-white',
    labelClassName: 'text-emerald-700',
    valueClassName: 'text-emerald-900',
    iconWrapperClassName: 'bg-emerald-100 text-emerald-700',
    icon: faCircleCheck,
  },
  {
    key: 'draft',
    label: 'پیش‌نویس',
    cardClassName: 'border-amber-100 from-amber-50 to-white',
    labelClassName: 'text-amber-700',
    valueClassName: 'text-amber-900',
    iconWrapperClassName: 'bg-amber-100 text-amber-700',
    icon: faPenToSquare,
  },
  {
    key: 'homepage',
    label: 'نمایش در خانه',
    cardClassName: 'border-indigo-100 from-indigo-50 to-white',
    labelClassName: 'text-indigo-700',
    valueClassName: 'text-indigo-900',
    iconWrapperClassName: 'bg-indigo-100 text-indigo-700',
    icon: faHouseChimney,
  },
  {
    key: 'enrollments',
    label: 'مجموع ثبت‌نام‌ها',
    cardClassName: 'border-violet-100 from-violet-50 to-white',
    labelClassName: 'text-violet-700',
    valueClassName: 'text-violet-900',
    iconWrapperClassName: 'bg-violet-100 text-violet-700',
    icon: faUsers,
  },
] as const;

const Courses: React.FC = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentUploadFile, setCurrentUploadFile] = useState<string>('');
  const [uploadedBytes, setUploadedBytes] = useState(0);
  const [totalBytes, setTotalBytes] = useState(0);
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [courseVideos, setCourseVideos] = useState<Video[]>([]);
  const [newVideos, setNewVideos] = useState<Array<{id: string, file: File | null, title: string}>>([]);
  const [courseAudios, setCourseAudios] = useState<Audio[]>([]);
  const [newAudios, setNewAudios] = useState<Array<{id: string, file: File | null, title: string, duration?: number}>>([]);
  const [editAttachments, setEditAttachments] = useState<Array<{id: string, url: string, fileName: string}>>([]);
  const [newAttachmentFiles, setNewAttachmentFiles] = useState<File[]>([]);
  const [newThumbnailFile, setNewThumbnailFile] = useState<File | null>(null);
  const [newIntroVideoFile, setNewIntroVideoFile] = useState<File | null>(null);
  const [newCourseModalTab, setNewCourseModalTab] = useState<
    'core' | 'about' | 'prerequisites' | 'curriculum' | 'practiceSessions'
  >('core');
  const [editCourseModalTab, setEditCourseModalTab] = useState<
    | 'core'
    | 'about'
    | 'prerequisites'
    | 'curriculum'
    | 'lessonUpdates'
    | 'practiceSessions'
    | 'faq'
    | 'satisfaction'
  >('core');
  const [newPrerequisites, setNewPrerequisites] = useState<
    Array<{ prerequisiteCourseId: string; requirementType: 'SUGGESTED' | 'MANDATORY' }>
  >([]);
  const [editPrerequisites, setEditPrerequisites] = useState<
    Array<{ prerequisiteCourseId: string; requirementType: 'SUGGESTED' | 'MANDATORY' }>
  >([]);
  const [newCourse, setNewCourse] = useState({
    title: '',
    description: '',
    longDescription: '',
    learningOutcomes: [] as string[],
    summaryBullets: [] as string[],
    courseCharacteristics: [] as CharacteristicRow[],
    categoryId: '',
    price: 0,
    discountType: 'NONE' as CourseDiscountType,
    discountPercent: 0,
    discountAmount: 0,
    discountStartsAt: null as string | null,
    discountEndsAt: null as string | null,
    courseLevel: '',
    suggestedDailyTime: '',
    teachingFormat: '',
    finalOutcomeLabel: '',
    published: false,
    showOnHomepage: true,
    allowComments: true,
    featuredPopularCarousel: false,
    featuredBestsellerCarousel: false,
    featuredNewestCarousel: false,
    isFreeCourse: false,
    thumbnail: null as File | null,
    video: null as File | null,
    introVideoAparatUrl: '',
    attachments: [] as File[],
    courseVideos: [] as Array<{id: string, file: File | null, title: string}>,
    courseAudios: [] as Array<{id: string, file: File | null, title: string, duration?: number}>,
  });
  const [isUsersModalOpen, setIsUsersModalOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [enrollments, setEnrollments] = useState<Array<{
    id: string;
    userId: string;
    courseId: string;
    enrolledAt: string;
    user: {
      id: string;
      username: string;
      email: string | null;
      phone: string | null;
      firstName: string | null;
      lastName: string | null;
      avatar: string | null;
    };
  }>>([]);
  const [loadingEnrollments, setLoadingEnrollments] = useState(false);
  const [enrollmentsPage, setEnrollmentsPage] = useState(1);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [targetCourseId, setTargetCourseId] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [courseCategories, setCourseCategories] = useState<ContentCategory[]>([]);
  const [courseListSearch, setCourseListSearch] = useState('');
  const [courseListPublished, setCourseListPublished] = useState<'' | 'published' | 'draft'>('');
  const [courseListHomepage, setCourseListHomepage] = useState<'' | 'yes' | 'no'>('');
  const [courseListCategoryId, setCourseListCategoryId] = useState('');

  const newCourseThumbnailInputRef = useRef<HTMLInputElement>(null);
  const editCourseThumbnailInputRef = useRef<HTMLInputElement>(null);

  const newCourseThumbnailPreviewUrl = useMemo(
    () => (newCourse.thumbnail ? URL.createObjectURL(newCourse.thumbnail) : null),
    [newCourse.thumbnail],
  );

  const editNewThumbnailPreviewUrl = useMemo(
    () => (newThumbnailFile ? URL.createObjectURL(newThumbnailFile) : null),
    [newThumbnailFile],
  );

  useEffect(() => {
    return () => {
      if (newCourseThumbnailPreviewUrl) URL.revokeObjectURL(newCourseThumbnailPreviewUrl);
    };
  }, [newCourseThumbnailPreviewUrl]);

  useEffect(() => {
    return () => {
      if (editNewThumbnailPreviewUrl) URL.revokeObjectURL(editNewThumbnailPreviewUrl);
    };
  }, [editNewThumbnailPreviewUrl]);

  const validEnrollments = useMemo(
    () => enrollments.filter((enrollment) => enrollment && enrollment.user),
    [enrollments],
  );

  const totalEnrollmentPages = useMemo(
    () => Math.max(1, Math.ceil(validEnrollments.length / ENROLLMENTS_PAGE_SIZE)),
    [validEnrollments],
  );

  const paginatedEnrollments = useMemo(() => {
    const start = (enrollmentsPage - 1) * ENROLLMENTS_PAGE_SIZE;
    return validEnrollments.slice(start, start + ENROLLMENTS_PAGE_SIZE);
  }, [enrollmentsPage, validEnrollments]);

  useEffect(() => {
    setEnrollmentsPage(1);
  }, [selectedCourse, enrollments]);

  useEffect(() => {
    if (enrollmentsPage > totalEnrollmentPages) {
      setEnrollmentsPage(totalEnrollmentPages);
    }
  }, [enrollmentsPage, totalEnrollmentPages]);

  // Helper functions for dynamic video/audio management
  const addVideoField = () => {
    setNewCourse({
      ...newCourse,
      courseVideos: [...newCourse.courseVideos, {id: Date.now().toString(), file: null, title: ''}]
    });
  };

  const removeVideoField = (id: string) => {
    setNewCourse({
      ...newCourse,
      courseVideos: newCourse.courseVideos.filter(v => v.id !== id)
    });
  };

  const updateVideoField = (id: string, field: 'file' | 'title', value: File | null | string) => {
    setNewCourse({
      ...newCourse,
      courseVideos: newCourse.courseVideos.map(v => 
        v.id === id ? {...v, [field]: value} : v
      )
    });
  };

  const getAudioFileDuration = (file: File): Promise<number> =>
    new Promise((resolve) => {
      const el = document.createElement('audio') as HTMLAudioElement;
      const url = URL.createObjectURL(file);
      el.onloadedmetadata = () => { URL.revokeObjectURL(url); resolve(Math.round(el.duration) || 0); };
      el.onerror = () => { URL.revokeObjectURL(url); resolve(0); };
      el.src = url;
    });

  const addAudioField = () => {
    setNewCourse({
      ...newCourse,
      courseAudios: [...newCourse.courseAudios, {id: Date.now().toString(), file: null, title: ''}]
    });
  };

  const removeAudioField = (id: string) => {
    setNewCourse((prev) => ({
      ...prev,
      courseAudios: prev.courseAudios.filter(a => a.id !== id)
    }));
  };

  const updateAudioField = async (id: string, field: 'file' | 'title', value: File | null | string) => {
    if (field === 'file' && value instanceof File) {
      const duration = await getAudioFileDuration(value);
      setNewCourse((prev) => ({
        ...prev,
        courseAudios: prev.courseAudios.map(a => a.id === id ? {...a, file: value, duration} : a)
      }));
    } else {
      setNewCourse((prev) => ({
        ...prev,
        courseAudios: prev.courseAudios.map(a => a.id === id ? {...a, [field]: value} : a)
      }));
    }
  };

  const addNewAudioField = () => {
    if (courseAudios.length + newAudios.length >= MAX_EPISODES_PER_TYPE) {
      setError(`حداکثر ${MAX_EPISODES_PER_TYPE} فایل صوتی می‌توانید برای این دوره ثبت کنید`);
      return;
    }
    setError('');
    setNewAudios((prev) => [...prev, { id: Date.now().toString(), file: null, title: '' }]);
  };

  const removeNewAudioField = (id: string) => {
    setNewAudios((prev) => prev.filter((audio) => audio.id !== id));
  };

  const updateNewAudioField = async (id: string, field: 'file' | 'title', value: File | null | string) => {
    if (field === 'file' && value instanceof File) {
      const duration = await getAudioFileDuration(value);
      setNewAudios((prev) =>
        prev.map((audio) => (audio.id === id ? { ...audio, file: value, duration } : audio))
      );
    } else {
      setNewAudios((prev) =>
        prev.map((audio) => (audio.id === id ? { ...audio, [field]: value } : audio))
      );
    }
  };

  const handleAttachmentSelection = (files: FileList | null) => {
    if (!files) return;
    setNewAttachmentFiles((prev) => [...prev, ...Array.from(files)]);
  };

  const handleRemoveAttachment = (attachmentId: string) => {
    setEditAttachments((prev) => prev.filter((attachment) => attachment.id !== attachmentId));
  };

  const handleRemoveNewAttachment = (index: number) => {
    setNewAttachmentFiles((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleVideoFieldChange = (
    videoId: string,
    field: keyof Pick<Video, 'title' | 'description' | 'order' | 'published'>,
    value: string | number | boolean
  ) => {
    setCourseVideos((prev) =>
      prev.map((video) =>
        video.id === videoId ? { ...video, [field]: field === 'order' ? Number(value) : value } : video
      )
    );
  };

  const handleSaveVideoChanges = async (video: Video) => {
    try {
      await videosService.update(video.id, {
        title: video.title,
        description: video.description,
        order: video.order,
        published: video.published,
      });
      fetchCourses();
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در به‌روزرسانی ویدیو');
    }
  };

  const handleAudioFieldChange = (
    audioId: string,
    field: keyof Pick<Audio, 'title' | 'description' | 'order' | 'published' | 'duration'>,
    value: string | number | boolean
  ) => {
    setCourseAudios((prev) =>
      prev.map((audio) =>
        audio.id === audioId ? { ...audio, [field]: (field === 'order' || field === 'duration') ? Number(value) : value } : audio
      )
    );
  };

  const handleAutoDetectDuration = async (audio: Audio) => {
    const url = audio.audioFile?.startsWith('http')
      ? audio.audioFile
      : `${API_ORIGIN}/uploads/${audio.audioFile}`;
    const duration = await getAudioFileDuration(new File([], '')).catch(() => 0);
    // Load from URL directly
    const detected = await new Promise<number>((resolve) => {
      const el = document.createElement('audio') as HTMLAudioElement;
      el.onloadedmetadata = () => resolve(Math.round(el.duration) || 0);
      el.onerror = () => resolve(0);
      el.src = url;
    });
    if (detected > 0) {
      handleAudioFieldChange(audio.id, 'duration', detected);
    } else {
      alert('نتوانست مدت زمان را تشخیص دهد. لطفاً به صورت دستی وارد کنید.');
    }
  };

  const handleSaveAudioChanges = async (audio: Audio) => {
    try {
      await audiosService.update(audio.id, {
        title: audio.title,
        description: audio.description,
        order: audio.order,
        published: audio.published,
        ...(audio.duration != null && audio.duration > 0 ? { duration: audio.duration } : {}),
      });
      fetchCourses();
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در به‌روزرسانی فایل صوتی');
    }
  };

  const handleDeleteAudio = async (audioId: string) => {
    if (!window.confirm('آیا از حذف این فایل صوتی اطمینان دارید؟')) {
      return;
    }

    try {
      await audiosService.delete(audioId);
      setCourseAudios((prev) => prev.filter((audio) => audio.id !== audioId));
      fetchCourses();
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در حذف فایل صوتی');
    }
  };

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const data = await coursesService.getAll();
      const transformedData = data.map((course) => {
        const pay = parseFloat(String(course.price)) || 0;
        const base =
          typeof course.listPrice === 'number' && !Number.isNaN(course.listPrice)
            ? course.listPrice
            : pay;
        return {
          ...course,
          price: base,
          listPrice: typeof course.listPrice === 'number' ? course.listPrice : base,
        };
      });
      setCourses(transformedData);
    } catch (err: any) {
        setError(err.response?.data?.message || 'خطا در دریافت دوره‌ها');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  const courseStats = useMemo(() => {
    const total = courses.length;
    const published = courses.filter((c) => c.published).length;
    const draft = total - published;
    const homepage = courses.filter((c) => c.showOnHomepage !== false).length;
    const enrollmentsSum = courses.reduce((sum, c) => sum + (c._count?.enrollments ?? 0), 0);
    return { total, published, draft, homepage, enrollmentsSum };
  }, [courses]);

  const filteredCourses = useMemo(() => {
    const q = courseListSearch.trim().toLowerCase();
    return courses.filter((course) => {
      if (q) {
        const inTitle = (course.title || '').toLowerCase().includes(q);
        const inDesc = (course.description || '').toLowerCase().includes(q);
        const inSlug = (course.slug || '').toLowerCase().includes(q);
        if (!inTitle && !inDesc && !inSlug) return false;
      }
      if (courseListPublished === 'published' && !course.published) return false;
      if (courseListPublished === 'draft' && course.published) return false;
      if (courseListHomepage === 'yes' && course.showOnHomepage === false) return false;
      if (courseListHomepage === 'no' && course.showOnHomepage !== false) return false;
      if (courseListCategoryId && (course.categoryId || '') !== courseListCategoryId) return false;
      return true;
    });
  }, [courses, courseListSearch, courseListPublished, courseListHomepage, courseListCategoryId]);

  const courseListActiveFilters = [
    courseListSearch.trim(),
    courseListPublished,
    courseListHomepage,
    courseListCategoryId,
  ].filter(Boolean).length;

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await categoriesService.getAll('COURSE');
        setCourseCategories(data.filter((item) => item.isActive));
      } catch (err) {
        console.error('Failed to load course categories', err);
      }
    };
    loadCategories();
  }, []);

  // Helper function to upload file with progress tracking
  const uploadFileWithProgress = async (
    url: string,
    formData: FormData,
    fileName: string,
    fileSize: number,
    previousBytes: number,
    totalSize: number,
    onProgress?: (progress: number) => void
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      setCurrentUploadFile(fileName);
      setTotalBytes(totalSize);
      setUploadedBytes(previousBytes);

      api.patch(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        // آپلود ویدیو/فایل دوره ممکن است چند دقیقه طول بکشد؛ timeout پیش‌فرض ۳۰ ثانیه را برای آپلود غیرفعال می‌کنیم.
        timeout: 0,
        onUploadProgress: (progressEvent: any) => {
          if (progressEvent.total) {
            const currentFilePercent = (progressEvent.loaded * 100) / progressEvent.total;
            const currentFileBytes = progressEvent.loaded;
            const totalUploadedBytes = previousBytes + currentFileBytes;
            
            setUploadedBytes(totalUploadedBytes);
            
            // Call progress callback if provided
            if (onProgress) {
              onProgress(currentFilePercent);
            }
          }
        },
      })
        .then(() => {
          resolve();
        })
        .catch((error: any) => {
          reject(error);
        });
    });
  };
  const handleAddCourse = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUploading(true);
    setUploadProgress(0);
    setError('');
    
    try {
      const learningOutcomes = newCourse.learningOutcomes.map((s) => s.trim()).filter(Boolean);
      const summaryBullets = newCourse.summaryBullets.map((s) => s.trim()).filter(Boolean);
      const courseCharacteristics = newCourse.courseCharacteristics
        .map((r) => ({ label: r.label.trim(), value: r.value.trim() }))
        .filter((r) => r.label && r.value);
      const prerequisites = newPrerequisites
        .filter((r) => r.prerequisiteCourseId.trim())
        .map((r) => ({
          prerequisiteCourseId: r.prerequisiteCourseId.trim(),
          requirementType: r.requirementType,
        }));
      const courseData = {
        title: newCourse.title,
        description: newCourse.description,
        longDescription: newCourse.longDescription.trim() || undefined,
        learningOutcomes,
        summaryBullets,
        ...(courseCharacteristics.length ? { courseCharacteristics } : {}),
        ...(prerequisites.length ? { prerequisites } : {}),
        categoryId: newCourse.categoryId || undefined,
        price: newCourse.price,
        discountType: newCourse.discountType,
        discountPercent: newCourse.discountType === 'PERCENT' ? newCourse.discountPercent : null,
        discountAmount: newCourse.discountType === 'FIXED' ? newCourse.discountAmount : null,
        discountStartsAt: newCourse.discountStartsAt || undefined,
        discountEndsAt: newCourse.discountEndsAt || undefined,
        published: newCourse.published,
        showOnHomepage: newCourse.showOnHomepage,
        allowComments: newCourse.allowComments,
        featuredPopularCarousel: newCourse.featuredPopularCarousel,
        featuredBestsellerCarousel: newCourse.featuredBestsellerCarousel,
        featuredNewestCarousel: newCourse.featuredNewestCarousel,
        isFreeCourse: newCourse.isFreeCourse,
        ...(newCourse.courseLevel.trim() ? { courseLevel: newCourse.courseLevel.trim() } : {}),
        ...(newCourse.suggestedDailyTime.trim()
          ? { suggestedDailyTime: newCourse.suggestedDailyTime.trim() }
          : {}),
        ...(newCourse.teachingFormat.trim()
          ? { teachingFormat: newCourse.teachingFormat.trim() }
          : {}),
        ...(newCourse.finalOutcomeLabel.trim()
          ? { finalOutcomeLabel: newCourse.finalOutcomeLabel.trim() }
          : {}),
        ...(newCourse.introVideoAparatUrl.trim()
          ? { introVideoAparatUrl: newCourse.introVideoAparatUrl.trim() }
          : {}),
      };

      const token = localStorage.getItem('token');
      // Use the API service's base URL which handles HTTPS conversion
      const response = await fetch(`${API_ORIGIN}/api/courses`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(courseData),
      });

      if (!response.ok) {
        throw new Error('خطا در ایجاد دوره');
      }

      const createdCourse = await response.json();
      
      // Calculate total files and their sizes
      let totalFiles = 0;
      let totalSize = 0;
      const filesToUpload: Array<{type: string, file: File, url: string}> = [];
      
      if (newCourse.thumbnail) {
        totalFiles++;
        totalSize += newCourse.thumbnail.size;
        filesToUpload.push({
          type: 'thumbnail',
          file: newCourse.thumbnail,
          url: `/courses/${createdCourse.id}/thumbnail`
        });
      }

      if (newCourse.video) {
        totalFiles++;
        totalSize += newCourse.video.size;
        filesToUpload.push({
          type: 'video',
          file: newCourse.video,
          url: `/courses/${createdCourse.id}/video`
        });
      }

      for (const attachment of newCourse.attachments) {
        totalFiles++;
        totalSize += attachment.size;
        filesToUpload.push({
          type: 'attachment',
          file: attachment,
          url: `/courses/${createdCourse.id}/attachments`
        });
      }

      for (const video of newCourse.courseVideos) {
        if (video.file) {
          totalFiles++;
          totalSize += video.file.size;
          filesToUpload.push({
            type: 'courseVideo',
            file: video.file,
            url: `/courses/${createdCourse.id}/courseVideos`
          });
        }
      }

      for (const audio of newCourse.courseAudios) {
        if (audio.file) {
          totalFiles++;
          totalSize += audio.file.size;
          filesToUpload.push({
            type: 'courseAudio',
            file: audio.file,
            url: `/courses/${createdCourse.id}/courseAudios`
          });
        }
      }

      // Upload all files with progress tracking
      let uploadedBytesTotal = 0;
      
      for (let i = 0; i < filesToUpload.length; i++) {
        const fileItem = filesToUpload[i];
        const formData = new FormData();
        formData.append(fileItem.type === 'courseVideo' ? 'courseVideos' : 
                       fileItem.type === 'courseAudio' ? 'courseAudios' :
                       fileItem.type === 'attachment' ? 'attachments' : fileItem.type, 
                       fileItem.file);
        
        await uploadFileWithProgress(
          fileItem.url,
          formData,
          fileItem.file.name,
          fileItem.file.size,
          uploadedBytesTotal,
          totalSize,
          (currentFileProgress: number) => {
            // Calculate overall progress: previous files + current file progress
            const overallProgress = ((uploadedBytesTotal + (fileItem.file.size * currentFileProgress / 100)) / totalSize) * 100;
            setUploadProgress(Math.min(100, overallProgress));
          }
        );
        
        uploadedBytesTotal += fileItem.file.size;
        setUploadProgress((uploadedBytesTotal / totalSize) * 100);
      }
      
      setUploadProgress(100);
      setCurrentUploadFile('');

      const courseAudiosWithFile = newCourse.courseAudios.filter(a => a.file);
      if (courseAudiosWithFile.length > 0) {
        const refreshedAudios = await audiosService.getAll(createdCourse.id);
        const sortedAudios = Array.isArray(refreshedAudios)
          ? refreshedAudios.sort((a: Audio, b: Audio) => (a.order ?? 0) - (b.order ?? 0))
          : [];
        const newlyCreatedAudios = sortedAudios.slice(-courseAudiosWithFile.length);
        await Promise.all(
          newlyCreatedAudios.map((audio, index) => {
            const desiredTitle = courseAudiosWithFile[index]?.title?.trim();
            const desiredDuration = courseAudiosWithFile[index]?.duration;
            const updateData: Record<string, any> = {};
            if (desiredTitle && desiredTitle !== audio.title) updateData.title = desiredTitle;
            if (desiredDuration && desiredDuration > 0) updateData.duration = desiredDuration;
            if (Object.keys(updateData).length > 0) return audiosService.update(audio.id, updateData);
            return Promise.resolve();
          })
        );
      }

      setNewCourse({
        title: '',
        description: '',
        longDescription: '',
        learningOutcomes: [],
        summaryBullets: [],
        courseCharacteristics: [],
        categoryId: '',
        price: 0,
        discountType: 'NONE' as CourseDiscountType,
        discountPercent: 0,
        discountAmount: 0,
        discountStartsAt: null,
        discountEndsAt: null,
        courseLevel: '',
        suggestedDailyTime: '',
        teachingFormat: '',
        finalOutcomeLabel: '',
        published: false,
        showOnHomepage: true,
        allowComments: true,
        featuredPopularCarousel: false,
        featuredBestsellerCarousel: false,
        featuredNewestCarousel: false,
        isFreeCourse: false,
        thumbnail: null,
        video: null,
        introVideoAparatUrl: '',
        attachments: [],
        courseVideos: [],
        courseAudios: [],
      });
      setNewPrerequisites([]);

      setIsModalOpen(false);
      setNewCourseModalTab('core');
      fetchCourses();
    } catch (err: any) {
      let errorMessage = 'خطا در ایجاد دوره';
      try {
        const errorData = JSON.parse(err.message);
        if (Array.isArray(errorData.message)) {
          errorMessage = errorData.message.join(', ');
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch {
        errorMessage = err.message || 'خطا در ایجاد دوره';
      }
      setError(errorMessage);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const mergeLegacyCourseCharacteristics = (c: Course): Course => {
    const existing = c.courseCharacteristics;
    if (
      Array.isArray(existing) &&
      existing.some((p) => (p?.label?.trim() || p?.value?.trim()))
    ) {
      return c;
    }
    const legacy: CharacteristicRow[] = [];
    if (c.courseLevel?.trim()) legacy.push({ label: 'سطح دوره', value: c.courseLevel.trim() });
    if (c.suggestedDailyTime?.trim()) legacy.push({ label: 'مدت پیشنهادی روزانه', value: c.suggestedDailyTime.trim() });
    if (c.teachingFormat?.trim()) legacy.push({ label: 'نوع آموزش', value: c.teachingFormat.trim() });
    if (c.finalOutcomeLabel?.trim()) legacy.push({ label: 'خروجی نهایی', value: c.finalOutcomeLabel.trim() });
    if (legacy.length) return { ...c, courseCharacteristics: legacy };
    return c;
  };

  type EditCourseInitialTab =
    | 'core'
    | 'about'
    | 'prerequisites'
    | 'curriculum'
    | 'lessonUpdates'
    | 'practiceSessions'
    | 'faq'
    | 'satisfaction';

  const handleEditCourse = async (
    course: Course,
    options?: { initialTab?: EditCourseInitialTab },
  ) => {
    const targetTab: EditCourseInitialTab = options?.initialTab ?? 'core';
    // لود ویدیو/صوت قبل از تب curriculum؛ تا آن‌وقت روی همان تب اولیه می‌مانیم
    setEditCourseModalTab(targetTab === 'curriculum' ? 'core' : targetTab);
    setIsEditModalOpen(true);
    setNewVideos([]);
    setNewAudios([]);
    setNewAttachmentFiles([]);
    setNewThumbnailFile(null);
    setNewIntroVideoFile(null);

    let courseForForm: Course = mergeLegacyCourseCharacteristics({ ...course });
    try {
      const full = await coursesService.getById(course.id);
      let merged = mergeLegacyCourseCharacteristics({ ...full });
      const basePrice =
        typeof merged.listPrice === 'number' ? merged.listPrice : Number(merged.price) || 0;
      merged = {
        ...merged,
        price: basePrice,
        discountType: merged.discountType ?? 'NONE',
        discountPercent: merged.discountPercent ?? 0,
        discountAmount: merged.discountAmount ?? 0,
        discountStartsAt: merged.discountStartsAt ?? null,
        discountEndsAt: merged.discountEndsAt ?? null,
        isFreeCourse: Boolean((merged as Course).isFreeCourse),
      };
      courseForForm = merged;
      setEditPrerequisites(
        (merged.prerequisites || [])
          .filter((p) => p.course?.id)
          .map((p) => ({
            prerequisiteCourseId: p.course!.id,
            requirementType: p.requirementType,
          })),
      );
    } catch (err) {
      console.error('Error loading course detail:', err);
      setEditPrerequisites([]);
    }
    setEditingCourse(courseForForm);

    const attachmentUrls = courseForForm.attachments || [];
    const attachmentFiles = (courseForForm as any).attachmentFiles || [];
    const combinedAttachments = attachmentUrls.map((url, index) => ({
      id: `${course.id}-attachment-${index}-${Date.now()}`,
      url,
      fileName: attachmentFiles[index] || url?.split('/').pop() || `attachment-${index + 1}`,
    }));
    setEditAttachments(combinedAttachments);
    
    // Fetch existing videos for this course
    try {
      const videos = await videosService.getAll(course.id);
      setCourseVideos(videos.sort((a, b) => a.order - b.order));
    } catch (err: any) {
      console.error('Error fetching course videos:', err);
      setCourseVideos([]);
    }

    try {
      const audios = await audiosService.getAll(course.id);
      const sortedAudios = Array.isArray(audios)
        ? audios.sort((a: Audio, b: Audio) => (a.order ?? 0) - (b.order ?? 0))
        : [];
      setCourseAudios(sortedAudios);
    } catch (err: any) {
      console.error('Error fetching course audios:', err);
      setCourseAudios([]);
    }

    setEditCourseModalTab(targetTab);
  };

  const handleDeleteVideo = async (videoId: string) => {
    if (!window.confirm('آیا از حذف این ویدیو اطمینان دارید؟')) {
      return;
    }

    try {
      await videosService.delete(videoId);
      setCourseVideos((prev) => prev.filter(v => v.id !== videoId));
      // Refresh courses list
      fetchCourses();
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در حذف ویدیو');
    }
  };

  const addNewVideoField = () => {
    if (courseVideos.length + newVideos.length >= MAX_EPISODES_PER_TYPE) {
      setError(`حداکثر ${MAX_EPISODES_PER_TYPE} ویدیو می‌توانید برای این دوره ثبت کنید`);
      return;
    }
    setError('');
    setNewVideos([...newVideos, {id: Date.now().toString(), file: null, title: ''}]);
  };

  const removeNewVideoField = (id: string) => {
    setNewVideos(newVideos.filter(v => v.id !== id));
  };

  const updateNewVideoField = (id: string, field: 'file' | 'title', value: File | null | string) => {
    setNewVideos(newVideos.map(v => 
      v.id === id ? {...v, [field]: value} : v
    ));
  };

  const handleDeleteCourse = async (courseId: string) => {
    if (!window.confirm('آیا از حذف این دوره اطمینان دارید؟')) {
      return;
    }

    try {
      await coursesService.delete(courseId);
      setCourses(courses.filter(course => course.id !== courseId));
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در حذف دوره');
    }
  };

  const handleUpdateCourse = async (e?: React.BaseSyntheticEvent) => {
    e?.preventDefault();
    if (!editingCourse) return;

    setIsUploading(true);
    setUploadProgress(0);
    setUploadedBytes(0);
    setTotalBytes(0);
    setError('');

    const courseId = editingCourse.id;
    const videosToUpload = newVideos.filter((video) => video.file);
    const audiosToUpload = newAudios.filter((audio) => audio.file);

    if (courseVideos.length + videosToUpload.length > MAX_EPISODES_PER_TYPE) {
      setError(`تعداد ویدیوهای دوره نمی‌تواند بیشتر از ${MAX_EPISODES_PER_TYPE} عدد باشد`);
      setIsUploading(false);
      return;
    }

    if (courseAudios.length + audiosToUpload.length > MAX_EPISODES_PER_TYPE) {
      setError(`تعداد فایل‌های صوتی دوره نمی‌تواند بیشتر از ${MAX_EPISODES_PER_TYPE} عدد باشد`);
      setIsUploading(false);
      return;
    }

    // Validate audio files before upload
    const allowedAudioTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/m4a', 'audio/aac', 'audio/flac', 'audio/webm', 'audio/opus'];
    const allowedAudioExtensions = ['.mp3', '.wav', '.ogg', '.m4a', '.aac', '.flac', '.webm', '.opus'];
    
    for (const audio of audiosToUpload) {
      if (!audio.file) continue;
      
      const fileExtension = audio.file.name.toLowerCase().substring(audio.file.name.lastIndexOf('.'));
      const isValidType = allowedAudioTypes.includes(audio.file.type);
      const isValidExtension = allowedAudioExtensions.some(ext => audio.file!.name.toLowerCase().endsWith(ext));
      
      if (!isValidType && !isValidExtension) {
        setError(`فایل صوتی "${audio.file.name}" فرمت نامعتبر دارد. فرمت‌های مجاز: mp3, wav, ogg, m4a, aac, flac, webm, opus`);
        setIsUploading(false);
        return;
      }
    }

    type PendingUpload = {
      url: string;
      fieldName: string;
      file: File;
      meta?: { title?: string };
    };

    const uploads: PendingUpload[] = [];

    if (newThumbnailFile) {
      uploads.push({
        url: `/courses/${courseId}/thumbnail`,
        fieldName: 'thumbnail',
        file: newThumbnailFile,
      });
    }

    if (newIntroVideoFile) {
      uploads.push({
        url: `/courses/${courseId}/video`,
        fieldName: 'video',
        file: newIntroVideoFile,
      });
    }

    newAttachmentFiles.forEach((file) => {
      uploads.push({
        url: `/courses/${courseId}/attachments`,
        fieldName: 'attachments',
        file,
      });
    });

    videosToUpload.forEach((video) => {
      if (!video.file) return;
      uploads.push({
        url: `/courses/${courseId}/courseVideos`,
        fieldName: 'courseVideos',
        file: video.file,
        meta: { title: video.title },
      });
    });

    audiosToUpload.forEach((audio) => {
      if (!audio.file) return;
      uploads.push({
        url: `/courses/${courseId}/courseAudios`,
        fieldName: 'courseAudios',
        file: audio.file,
        meta: { title: audio.title },
      });
    });

    const totalSize = uploads.reduce((sum, upload) => sum + (upload.file?.size || 0), 0);
    if (totalSize > 0) {
      setTotalBytes(totalSize);
    }

    try {
      const attachmentsToKeep = editAttachments
        .map((attachment) => attachment.fileName)
        .filter((name): name is string => Boolean(name));

      const courseCharacteristics = (editingCourse.courseCharacteristics ?? [])
        .map((r) => ({ label: (r.label ?? '').trim(), value: (r.value ?? '').trim() }))
        .filter((r) => r.label && r.value);

      const prerequisites = editPrerequisites
        .filter((r) => r.prerequisiteCourseId.trim())
        .map((r) => ({
          prerequisiteCourseId: r.prerequisiteCourseId.trim(),
          requirementType: r.requirementType,
        }));

      const satisfactionKpisClean = (
        Array.isArray(editingCourse.satisfactionKpis) ? editingCourse.satisfactionKpis : []
      )
        .map((x) => ({
          label: String(x.label ?? '').trim(),
          valueFa: String(x.valueFa ?? '').trim(),
        }))
        .filter((x) => x.label && x.valueFa);

      const updatePayload = {
        title: editingCourse.title,
        description: editingCourse.description,
        longDescription: editingCourse.longDescription ?? '',
        learningOutcomes: (editingCourse.learningOutcomes ?? []).map((s) => s.trim()).filter(Boolean),
        summaryBullets: (editingCourse.summaryBullets ?? []).map((s) => s.trim()).filter(Boolean),
        courseCharacteristics,
        prerequisites,
        categoryId: editingCourse.categoryId || null,
        price: editingCourse.price,
        discountType: editingCourse.discountType ?? 'NONE',
        discountPercent:
          editingCourse.discountType === 'PERCENT' ? (editingCourse.discountPercent ?? 0) : null,
        discountAmount:
          editingCourse.discountType === 'FIXED' ? (editingCourse.discountAmount ?? 0) : null,
        discountStartsAt: editingCourse.discountStartsAt ?? null,
        discountEndsAt: editingCourse.discountEndsAt ?? null,
        published: editingCourse.published,
        showOnHomepage: editingCourse.showOnHomepage ?? true,
        allowComments: editingCourse.allowComments ?? true,
        featuredPopularCarousel: Boolean(editingCourse.featuredPopularCarousel),
        featuredBestsellerCarousel: Boolean(editingCourse.featuredBestsellerCarousel),
        featuredNewestCarousel: Boolean(editingCourse.featuredNewestCarousel),
        isFreeCourse: Boolean(editingCourse.isFreeCourse),
        attachments: attachmentsToKeep,
        courseLevel: (editingCourse.courseLevel ?? '').trim() || null,
        suggestedDailyTime: (editingCourse.suggestedDailyTime ?? '').trim() || null,
        teachingFormat: (editingCourse.teachingFormat ?? '').trim() || null,
        finalOutcomeLabel: (editingCourse.finalOutcomeLabel ?? '').trim() || null,
        introVideoAparatUrl: (editingCourse.introVideoAparatUrl ?? '').trim() || null,
        satisfactionAvgLabel: (editingCourse.satisfactionAvgLabel ?? '').trim() || null,
        satisfactionKpis: satisfactionKpisClean.length ? satisfactionKpisClean : null,
      };

      const updatedCourse = await coursesService.update(courseId, updatePayload as any);
      setCourses((prev) =>
        prev.map((course) => (course.id === courseId ? updatedCourse : course))
      );

      let uploadedBytesTotal = 0;

      for (const upload of uploads) {
        const fileSize = upload.file.size || 0;
        const formData = new FormData();
        formData.append(upload.fieldName, upload.file);

        try {
          await uploadFileWithProgress(
            upload.url,
            formData,
            upload.file.name,
            fileSize,
            uploadedBytesTotal,
            totalSize || fileSize,
            (currentFileProgress: number) => {
              if (!totalSize) {
                setUploadProgress(currentFileProgress);
                return;
              }
              const overallProgress =
                ((uploadedBytesTotal + (fileSize * currentFileProgress) / 100) / totalSize) * 100;
              setUploadProgress(Math.min(100, overallProgress));
            }
          );

          uploadedBytesTotal += fileSize;
          setUploadedBytes(uploadedBytesTotal);
          if (totalSize) {
            setUploadProgress((uploadedBytesTotal / totalSize) * 100);
          } else {
            setUploadProgress(100);
          }
        } catch (uploadError: any) {
          // Extract error message from upload error
          const errorMessage = uploadError?.response?.data?.message || 
                              uploadError?.message || 
                              `خطا در اپلود فایل "${upload.file.name}"`;
          throw new Error(errorMessage);
        }
      }

      if (videosToUpload.length > 0) {
        const refreshedVideos = await videosService.getAll(courseId);
        const sortedVideos = refreshedVideos.sort((a, b) => a.order - b.order);
        const newlyCreatedVideos = sortedVideos.slice(-videosToUpload.length);
        await Promise.all(
          newlyCreatedVideos.map((video, index) => {
            const desiredTitle = videosToUpload[index]?.title?.trim();
            if (desiredTitle && desiredTitle !== video.title) {
              return videosService.update(video.id, { title: desiredTitle });
            }
            return Promise.resolve();
          })
        );
      }

      if (audiosToUpload.length > 0) {
        const refreshedAudios = await audiosService.getAll(courseId);
        const sortedAudios = Array.isArray(refreshedAudios)
          ? refreshedAudios.sort((a: Audio, b: Audio) => (a.order ?? 0) - (b.order ?? 0))
          : [];
        const newlyCreatedAudios = sortedAudios.slice(-audiosToUpload.length);
        await Promise.all(
          newlyCreatedAudios.map((audio, index) => {
            const desiredTitle = audiosToUpload[index]?.title?.trim();
            const desiredDuration = audiosToUpload[index]?.duration;
            const updateData: Record<string, any> = {};
            if (desiredTitle && desiredTitle !== audio.title) updateData.title = desiredTitle;
            if (desiredDuration && desiredDuration > 0) updateData.duration = desiredDuration;
            if (Object.keys(updateData).length > 0) return audiosService.update(audio.id, updateData);
            return Promise.resolve();
          })
        );
      }

      await fetchCourses();

      setIsEditModalOpen(false);
      setEditingCourse(null);
      setCourseVideos([]);
      setCourseAudios([]);
      setNewVideos([]);
      setNewAudios([]);
      setEditAttachments([]);
      setNewAttachmentFiles([]);
      setNewThumbnailFile(null);
      setNewIntroVideoFile(null);
      setEditCourseModalTab('core');
      setCurrentUploadFile('');
      setUploadProgress(0);
    } catch (err: any) {
      const message =
        err.response?.data?.message ??
        err.message ??
        'خطا در ویرایش دوره';
      setError(Array.isArray(message) ? message.join(', ') : message);
    } finally {
      setIsUploading(false);
      setUploadedBytes(0);
      setTotalBytes(0);
      setCurrentUploadFile('');
    }
  };

  const handleOpenUsersModal = async (course: Course) => {
    try {
      setSelectedCourse(course);
      setIsUsersModalOpen(true);
      setLoadingEnrollments(true);
      setEnrollmentsPage(1);
      setSelectedUserIds(new Set());
      setTargetCourseId('');
      setError(''); // Clear any previous errors
      
      const data = await coursesService.getEnrollments(course.id);
      // Backend returns { course, enrollments, total }
      if (data && Array.isArray(data.enrollments)) {
        setEnrollments(data.enrollments);
      } else if (Array.isArray(data)) {
        // Fallback for backward compatibility
        setEnrollments(data);
      } else {
        console.error('Invalid enrollments data format:', data);
        setEnrollments([]);
        setError('فرمت داده‌های دریافتی نامعتبر است');
      }
    } catch (err: any) {
      console.error('Error fetching enrollments:', err);
      setError(err.response?.data?.message || 'خطا در دریافت کاربران دوره');
      setEnrollments([]);
    } finally {
      setLoadingEnrollments(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedUserIds.size === validEnrollments.length) {
      setSelectedUserIds(new Set());
    } else {
      setSelectedUserIds(new Set(validEnrollments.map(e => e.userId)));
    }
  };

  const handleToggleUser = (userId: string) => {
    const newSelected = new Set(selectedUserIds);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUserIds(newSelected);
  };

  const handleTransferUsers = async () => {
    if (!selectedCourse || !targetCourseId) {
      setError('لطفا دوره مقصد را انتخاب کنید');
      return;
    }

    if (validEnrollments.length === 0) {
      setError('هیچ کاربری برای انتقال وجود ندارد');
      return;
    }

    if (!window.confirm(`آیا مطمئن هستید که می‌خواهید همه ${validEnrollments.length} کاربر را از دوره "${selectedCourse.title}" به دوره انتخابی منتقل کنید؟`)) {
      return;
    }

    setTransferring(true);
    setError('');

    try {
      // Transfer all enrollments from source course to target course
      // The backend will handle the transfer of all users
      const result = await coursesService.transferEnrollments(selectedCourse.id, targetCourseId);
      
      // Refresh enrollments list
      const data = await coursesService.getEnrollments(selectedCourse.id);
      if (data && Array.isArray(data.enrollments)) {
        setEnrollments(data.enrollments);
      } else if (Array.isArray(data)) {
        setEnrollments(data);
      } else {
        setEnrollments([]);
      }
      setSelectedUserIds(new Set());
      setTargetCourseId('');
      
      alert(result.message || `با موفقیت ${result.transferredCount} کاربر منتقل شد`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در انتقال کاربران');
    } finally {
      setTransferring(false);
    }
  };

  const AddButton = () => (
    <button
      type="button"
      onClick={() => {
        setNewCourseModalTab('core');
        setNewPrerequisites([]);
        setIsModalOpen(true);
      }}
      className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-indigo-700 transition-colors"
    >
      <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
      </svg>
      دوره جدید
    </button>
  );

  const SchoolIcon = () => (
    <svg className="h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  );

  const clearCourseListFilters = () => {
    setCourseListSearch('');
    setCourseListPublished('');
    setCourseListHomepage('');
    setCourseListCategoryId('');
  };

  return (
    <div className="space-y-5">
      <div className="rounded-2xl bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">مدیریت دوره‌ها</h1>
            <p className="mt-1 text-sm text-gray-600">
              فهرست همهٔ دوره‌ها؛ سرفصل‌ها و جلسات را با «سرفصل‌ها» یا با ویرایش دوره و تب «سرفصل‌ها» مدیریت کنید.
            </p>
          </div>
          <AddButton />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
        {courseOverviewCards.map((item) => {
          const value =
            item.key === 'total'
              ? courseStats.total
              : item.key === 'published'
                ? courseStats.published
                : item.key === 'draft'
                  ? courseStats.draft
                  : item.key === 'homepage'
                    ? courseStats.homepage
                    : courseStats.enrollmentsSum;
          return (
            <div key={item.key} className={`rounded-2xl border bg-gradient-to-b p-4 ${item.cardClassName}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className={`text-xs font-semibold ${item.labelClassName}`}>{item.label}</p>
                  <p className={`mt-2 text-2xl font-black ${item.valueClassName}`}>{value.toLocaleString('fa-IR')}</p>
                </div>
                <span className={`inline-flex h-9 w-9 items-center justify-center rounded-xl ${item.iconWrapperClassName}`}>
                  <FontAwesomeIcon icon={item.icon} className="h-4 w-4" />
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-3 rounded-2xl bg-white p-4 shadow-sm sm:grid-cols-2 xl:grid-cols-6">
        <div className="sm:col-span-2 xl:col-span-6 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => {
              clearCourseListFilters();
              setCourseListPublished('published');
            }}
            className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
          >
            فقط منتشر شده
          </button>
          <button
            type="button"
            onClick={() => {
              clearCourseListFilters();
              setCourseListPublished('draft');
            }}
            className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-100"
          >
            فقط پیش‌نویس
          </button>
          <button
            type="button"
            onClick={() => {
              clearCourseListFilters();
              setCourseListHomepage('yes');
            }}
            className="rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
          >
            در صفحهٔ اصلی
          </button>
          <button
            type="button"
            onClick={() => {
              clearCourseListFilters();
              setCourseListPublished('published');
              setCourseListHomepage('yes');
            }}
            className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-100"
          >
            منتشر + خانه
          </button>
          {courseListActiveFilters > 0 ? (
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-600">
              {courseListActiveFilters.toLocaleString('fa-IR')} فیلتر فعال
            </span>
          ) : null}
        </div>
        <input
          className="sm:col-span-2 rounded-xl border border-gray-200 px-3 py-2 text-sm"
          placeholder="جستجو: عنوان، توضیح کوتاه یا اسلاگ"
          value={courseListSearch}
          onChange={(e) => setCourseListSearch(e.target.value)}
        />
        <select
          className="rounded-xl border border-gray-200 px-3 py-2 text-sm"
          value={courseListPublished}
          onChange={(e) =>
            setCourseListPublished(e.target.value as typeof courseListPublished | '')
          }
        >
          <option value="">وضعیت: همه</option>
          <option value="published">منتشر شده</option>
          <option value="draft">پیش‌نویس</option>
        </select>
        <select
          className="rounded-xl border border-gray-200 px-3 py-2 text-sm"
          value={courseListHomepage}
          onChange={(e) => setCourseListHomepage(e.target.value as typeof courseListHomepage | '')}
        >
          <option value="">صفحه اصلی: همه</option>
          <option value="yes">نمایش در خانه</option>
          <option value="no">مخفی از خانه</option>
        </select>
        <select
          className="min-w-[12rem] appearance-none rounded-xl border border-gray-200 bg-white pe-9 ps-3 py-2 text-sm"
          value={courseListCategoryId}
          onChange={(e) => setCourseListCategoryId(e.target.value)}
        >
          <option value="">دستهٔ محتوا: همه</option>
          {courseCategories.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.name}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => void fetchCourses()}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
        >
          {loading ? <BrandSpinnerRing size="sm" inverse /> : <RefreshCw className="h-4 w-4" aria-hidden />}
          بروزرسانی لیست
        </button>
        <button
          type="button"
          onClick={clearCourseListFilters}
          className="inline-flex items-center justify-center rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
        >
          پاک‌کردن فیلترها
        </button>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <div className="rounded-2xl bg-white p-4 shadow-sm">
        {loading ? (
          <div className="overflow-hidden rounded-2xl border border-indigo-100 bg-gradient-to-b from-indigo-50/60 via-white to-white">
            <div className="flex items-center justify-between border-b border-indigo-100/70 px-4 py-3">
              <div className="flex items-center gap-2 text-indigo-700">
                <BrandSpinnerRing size="sm" />
                <span className="text-sm font-bold">در حال بارگذاری دوره‌ها</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-indigo-500 animate-bounce" />
                <span className="h-2 w-2 rounded-full bg-violet-500 animate-bounce [animation-delay:120ms]" />
                <span className="h-2 w-2 rounded-full bg-fuchsia-500 animate-bounce [animation-delay:240ms]" />
              </div>
            </div>
            <div className="space-y-2 p-3">
              {Array.from({ length: 6 }).map((_, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-12 items-center gap-2 rounded-xl border border-slate-100 bg-white px-3 py-2.5 animate-pulse"
                >
                  <div className="col-span-3 h-4 rounded bg-slate-200" />
                  <div className="col-span-2 h-4 rounded bg-slate-200" />
                  <div className="col-span-2 h-4 rounded bg-slate-200" />
                  <div className="col-span-1 h-4 rounded bg-slate-200" />
                  <div className="col-span-1 h-4 rounded bg-slate-200" />
                  <div className="col-span-2 h-4 rounded bg-slate-200" />
                  <div className="col-span-1 h-8 rounded-lg bg-indigo-100/70" />
                </div>
              ))}
            </div>
          </div>
        ) : courses.length === 0 ? (
          <div className="rounded-xl py-12">
            <EmptyState
              icon={<SchoolIcon />}
              title="دوره‌ای یافت نشد"
              description="هنوز دوره‌ای ثبت نشده است."
              action={<AddButton />}
            />
          </div>
        ) : filteredCourses.length === 0 ? (
          <div className="py-10 text-center text-sm text-gray-500">
            <p className="font-semibold text-gray-700">هیچ دوره‌ای با این فیلترها یافت نشد.</p>
            <button type="button" onClick={clearCourseListFilters} className="mt-3 rounded-xl border border-gray-200 px-4 py-2 text-xs font-bold text-gray-700 hover:bg-gray-50">
              پاک‌کردن فیلترها
            </button>
          </div>
        ) : (
          <div>
            <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-right font-semibold text-gray-700">دوره</th>
                  <th className="px-3 py-3 text-right font-semibold text-gray-700">ویدیوها</th>
                  <th className="px-3 py-3 text-right font-semibold text-gray-700">دسته‌بندی</th>
                  <th className="px-3 py-3 text-right font-semibold text-gray-700">وضعیت</th>
                  <th className="px-3 py-3 text-right font-semibold text-gray-700">صفحه اصلی</th>
                  <th className="px-3 py-3 text-right font-semibold text-gray-700">تاریخ ایجاد</th>
                  <th className="px-3 py-3 text-right font-semibold text-gray-700">کاربران</th>
                  <th className="px-3 py-3 text-right font-semibold text-gray-700">عملیات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {filteredCourses.map((course) => (
                  <tr key={course.id} className="hover:bg-gray-50/50">
                    <td className="whitespace-nowrap px-3 py-3">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          {course.thumbnail ? (
                            <img
                              src={course.thumbnail}
                              alt={course.title}
                              className="h-10 w-10 rounded-lg object-cover"
                              crossOrigin="anonymous"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-lg bg-blue-500 flex items-center justify-center text-white font-medium">                            
                              {course.title?.[0] || 'C'}
                            </div>
                          )}
                        </div>
                        <div className="mr-4 min-w-0 flex-1">
                          <div className="truncate text-sm font-semibold text-gray-900">{course.title}</div>
                          {course.slug ? (
                            <p className="truncate text-[11px] text-gray-400" title={course.slug}>
                              {course.slug}
                            </p>
                          ) : null}
                          <div className="line-clamp-2 text-xs text-gray-500">
                            {course.description ? truncateWords(course.description, 20) : '—'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-3">
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm text-gray-900">
                            {course.videos?.length || 0} ویدیو
                          </span>
                          {course.videoFile && (
                            <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                              معرفی
                            </span>
                          )}
                          {course.audios && course.audios.length > 0 && (
                            <span className="text-sm text-purple-700">
                              {course.audios.length} فایل صوتی
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-3">
                      <span className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-700">
                        {course.category?.name || '-'}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        course.published 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {course.published ? 'منتشر شده' : 'پیش‌نویس'}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        course.showOnHomepage !== false
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {course.showOnHomepage !== false ? '✓ نمایش' : '✗ مخفی'}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500">
                      {new Date(course.createdAt).toLocaleDateString('fa-IR')}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-sm font-medium">
                      <button
                        onClick={() => handleOpenUsersModal(course)}
                        className="text-purple-600 hover:text-purple-900 px-3 py-1 rounded hover:bg-purple-50 flex items-center gap-1 border border-purple-200"
                        title="مدیریت کاربران"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                        <span>کاربران</span>
                      </button>
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-sm font-medium">
                      <div className="flex flex-wrap items-center gap-2 justify-end">
                        <a
                          href={getPublicCourseDetailUrl(course)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-emerald-800 hover:text-emerald-950 px-2 py-1 rounded-md hover:bg-emerald-50 border border-emerald-200 text-xs font-medium"
                          title={
                            course.published
                              ? 'باز کردن صفحهٔ این دوره در قالب سایت (تب جدید)'
                              : 'پیش‌نمایش قالب؛ اگر دوره پیش‌نویس است، ممکن است در سایت عمومی در دسترس نباشد'
                          }
                        >
                          <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                            />
                          </svg>
                          نمایش در سایت
                        </a>
                        <button
                          type="button"
                          onClick={() => handleEditCourse(course, { initialTab: 'curriculum' })}
                          className="inline-flex items-center gap-1 text-teal-700 hover:text-teal-900 px-2 py-1 rounded-md hover:bg-teal-50 border border-teal-200 text-xs font-medium"
                          title="سرفصل‌ها و محتوای جلسات"
                        >
                          <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                          </svg>
                          سرفصل‌ها
                        </button>
                        <button 
                          onClick={() => handleEditCourse(course)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                          title="ویرایش"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button 
                          onClick={() => handleDeleteCourse(course.id)}
                          className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                          title="حذف"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-3 text-sm text-gray-600">
              <span>
                نمایش {filteredCourses.length.toLocaleString('fa-IR')} دوره
                {courseListActiveFilters > 0 && filteredCourses.length !== courses.length
                  ? ` از ${courses.length.toLocaleString('fa-IR')} دورهٔ بارگذاری‌شده`
                  : ''}
              </span>
            </div>
          </div>
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setNewCourseModalTab('core');
          setNewPrerequisites([]);
        }}
        title="دوره جدید"
        size="wide"
      >
        {isUploading && (
          <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-blue-900">در حال آپلود فایل‌ها...</span>
              <span className="text-sm font-semibold text-blue-700">{Math.round(uploadProgress)}%</span>
            </div>
            {currentUploadFile && (
              <p className="text-xs text-blue-700 mb-2 truncate" title={currentUploadFile}>
                📁 {currentUploadFile}
              </p>
            )}
            <ProgressBar progress={uploadProgress} className="mb-2" />
            {uploadedBytes > 0 && totalBytes > 0 && (
              <div className="flex justify-between text-xs text-blue-600">
                <span>{(uploadedBytes / (1024 * 1024)).toFixed(2)} MB</span>
                <span>از {(totalBytes / (1024 * 1024)).toFixed(2)} MB</span>
              </div>
            )}
          </div>
        )}
        
        <form onSubmit={handleAddCourse} className="space-y-4">
          <div className="-mx-4 sm:-mx-6 flex flex-wrap gap-0 border-b border-slate-200/90 bg-[#fafafa] px-2 sm:px-3 mb-1">
            <button
              type="button"
              onClick={() => setNewCourseModalTab('core')}
              className={`relative flex-1 min-h-[48px] min-w-[120px] px-2 sm:px-3 py-3 text-sm font-bold transition-colors ${
                newCourseModalTab === 'core'
                  ? 'text-[#1C3B9B] after:absolute after:bottom-0 after:left-3 after:right-3 after:h-[3px] after:rounded-t after:bg-[#1C3B9B]'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              اطلاعات پایه و فایل‌ها
            </button>
            <button
              type="button"
              onClick={() => setNewCourseModalTab('about')}
              className={`relative flex-1 min-h-[48px] min-w-[120px] px-2 sm:px-3 py-3 text-sm font-bold transition-colors ${
                newCourseModalTab === 'about'
                  ? 'text-[#1C3B9B] after:absolute after:bottom-0 after:left-3 after:right-3 after:h-[3px] after:rounded-t after:bg-[#1C3B9B]'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              محتوای «درباره دوره» (سایت)
            </button>
            <button
              type="button"
              onClick={() => setNewCourseModalTab('prerequisites')}
              className={`relative flex-1 min-h-[48px] min-w-[120px] px-2 sm:px-3 py-3 text-sm font-bold transition-colors ${
                newCourseModalTab === 'prerequisites'
                  ? 'text-[#1C3B9B] after:absolute after:bottom-0 after:left-3 after:right-3 after:h-[3px] after:rounded-t after:bg-[#1C3B9B]'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              پیش‌نیازها
            </button>
            <button
              type="button"
              onClick={() => setNewCourseModalTab('curriculum')}
              className={`relative flex-1 min-h-[48px] min-w-[120px] px-2 sm:px-3 py-3 text-sm font-bold transition-colors ${
                newCourseModalTab === 'curriculum'
                  ? 'text-[#1C3B9B] after:absolute after:bottom-0 after:left-3 after:right-3 after:h-[3px] after:rounded-t after:bg-[#1C3B9B]'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              سرفصل‌ها و جلسات
            </button>
            <button
              type="button"
              onClick={() => setNewCourseModalTab('practiceSessions')}
              className={`relative flex-1 min-h-[48px] min-w-[120px] px-2 sm:px-3 py-3 text-sm font-bold transition-colors ${
                newCourseModalTab === 'practiceSessions'
                  ? 'text-[#1C3B9B] after:absolute after:bottom-0 after:left-3 after:right-3 after:h-[3px] after:rounded-t after:bg-[#1C3B9B]'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              تمرین‌ها و جزوات
            </button>
          </div>

          {newCourseModalTab === 'core' && (
          <div className="space-y-5">
            <CoreFormRibbon mode="create" />

            <CoreSection
              title="عنوان و معرفی کوتاه"
              subtitle="خلاصه‌ای برای کارت، لیست و نتایج جستجو؛ متن بلند و کارت ویژگی‌ها در تب «درباره دوره» قابل تنظیم است."
              accent="brand"
            >
              <div>
                <label className={coreFieldClasses.label}>عنوان دوره</label>
                <input
                  type="text"
                  value={newCourse.title}
                  onChange={(e) => setNewCourse({ ...newCourse, title: e.target.value })}
                  className={coreFieldClasses.input}
                  required
                />
              </div>
              <div>
                <label className={coreFieldClasses.label}>توضیحات کوتاه</label>
                <textarea
                  value={newCourse.description}
                  onChange={(e) => setNewCourse({ ...newCourse, description: e.target.value })}
                  className={coreFieldClasses.textarea}
                  rows={3}
                  maxLength={2000}
                />
                <p className={coreFieldClasses.counter}>
                  {newCourse.description.length}/۲۰۰۰ کاراکتر
                </p>
              </div>
            </CoreSection>

            <CoreSection
              title="فروش و ویژگی‌های سریع"
              subtitle="دسته و قیمت؛ فیلدهای زیر در API ذخیره می‌شوند و با کارت‌های تب درباره ترکیب می‌شوند در صورت نیاز."
              accent="violet"
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={coreFieldClasses.label}>دسته‌بندی محتوا</label>
                  <select
                    value={newCourse.categoryId}
                    onChange={(e) => setNewCourse({ ...newCourse, categoryId: e.target.value })}
                    className={coreFieldClasses.select}
                  >
                    <option value="">بدون دسته‌بندی</option>
                    {courseCategories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.path || category.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <CourseCoreFreeCourseToggle
                idPrefix="new-course"
                isFreeCourse={newCourse.isFreeCourse}
                onChange={(nextFree) =>
                  setNewCourse((prev) =>
                    nextFree
                      ? {
                          ...prev,
                          isFreeCourse: true,
                          price: 0,
                          discountType: 'NONE',
                          discountPercent: 0,
                          discountAmount: 0,
                          discountStartsAt: null,
                          discountEndsAt: null,
                          featuredBestsellerCarousel: false,
                        }
                      : { ...prev, isFreeCourse: false },
                  )
                }
              />
              <CoursePricingAdminBlock
                locked={newCourse.isFreeCourse}
                values={{
                  listPrice: newCourse.price,
                  discountType: newCourse.discountType,
                  discountPercent: newCourse.discountPercent,
                  discountAmount: newCourse.discountAmount,
                  discountStartsAt: newCourse.discountStartsAt,
                  discountEndsAt: newCourse.discountEndsAt,
                }}
                onChange={(patch) =>
                  setNewCourse((prev) => {
                    const next = { ...prev, ...patch };
                    if (patch.listPrice !== undefined) {
                      next.price = patch.listPrice;
                    }
                    return next;
                  })
                }
              />
              <CourseCoreQuickSpecFields
                values={{
                  courseLevel: newCourse.courseLevel,
                  suggestedDailyTime: newCourse.suggestedDailyTime,
                  teachingFormat: newCourse.teachingFormat,
                  finalOutcomeLabel: newCourse.finalOutcomeLabel,
                }}
                onChange={(next) =>
                  setNewCourse({
                    ...newCourse,
                    courseLevel: next.courseLevel,
                    suggestedDailyTime: next.suggestedDailyTime,
                    teachingFormat: next.teachingFormat,
                    finalOutcomeLabel: next.finalOutcomeLabel,
                  })
                }
              />
            </CoreSection>

            <CoreSection
              title="انتشار، صفحه اصلی و نظرات"
              subtitle="نمایش در فروشگاه، خانه و نظر؛ کاروسل «رایگان» فقط با تیک «دوره رایگان» در بخش فروش."
              accent="amber"
            >
              <CourseCorePublishToggles
                idPrefix="new-course"
                published={newCourse.published}
                showOnHomepage={newCourse.showOnHomepage}
                allowComments={newCourse.allowComments}
                onChange={(field, value) =>
                  setNewCourse({ ...newCourse, [field]: value })
                }
              />
              <div className="border-t border-slate-100 pt-4">
                <p className="mb-3 text-xs font-bold text-slate-800">کاروسل‌های صفحهٔ اصلی (غیر از رایگان)</p>
                <p className="mb-3 text-[11px] leading-relaxed text-slate-500">
                  جدیدترین، پرطرفدار و پرفروش را اینجا فعال می‌کنید؛ دورهٔ رایگان در کاروسل مخصوص آن به‌‌طور خودکار از تیک «این دوره رایگان است» کنترل می‌شود.
                </p>
                <CourseCoreHomepageCarouselToggles
                  idPrefix="new-course-carousel"
                  includeBestsellerCarousel={!newCourse.isFreeCourse}
                  featuredPopularCarousel={newCourse.featuredPopularCarousel}
                  featuredBestsellerCarousel={newCourse.featuredBestsellerCarousel}
                  featuredNewestCarousel={newCourse.featuredNewestCarousel}
                  onChange={(field, value) => setNewCourse({ ...newCourse, [field]: value })}
                />
              </div>
            </CoreSection>

            <CoreSection
              title="فایل‌های اصلی رسانه"
              subtitle="قلب بصری و معرفی دوره پیش از اپیزودها؛ تصویر و ویدیو معرفی اختیاری‌اند، ضمیمه‌ها برای PDF و بایگانی دانشجو."
              accent="slate"
            >
              <div>
                <label className={coreFieldClasses.label}>تصویر شاخص</label>
                <p className="mb-2 rounded-lg border border-sky-100 bg-sky-50/80 px-3 py-2 text-[11px] leading-relaxed text-slate-700">
                  <span className="font-bold text-sky-900">راهنمای ابعاد (فرانت): </span>
                  {COURSE_THUMBNAIL_HINT_FA}
                </p>
                {newCourseThumbnailPreviewUrl ? (
                  <div className="mb-3 flex flex-wrap items-center gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <img
                      src={newCourseThumbnailPreviewUrl}
                      alt="پیش‌نمایش تصویر شاخص"
                      className="h-36 w-36 rounded-xl border border-white object-cover shadow-md sm:h-40 sm:w-40"
                    />
                    <div className="min-w-0 flex-1 space-y-2">
                      <p className="text-sm font-semibold text-slate-800">پیش‌نمایش تصویر انتخاب‌شده</p>
                      <p className="truncate text-xs text-slate-600" title={newCourse.thumbnail?.name}>
                        {newCourse.thumbnail?.name}
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          setNewCourse((prev) => ({ ...prev, thumbnail: null }));
                          if (newCourseThumbnailInputRef.current) newCourseThumbnailInputRef.current.value = '';
                        }}
                        className="rounded-xl border border-red-200 bg-white px-3 py-1.5 text-xs font-bold text-red-600 hover:bg-red-50"
                      >
                        حذف تصویر
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="mb-2 text-xs text-slate-500">بدون تصویر — پس از انتخاب فایل، پیش‌نمایش اینجا نمایش داده می‌شود.</p>
                )}
                <input
                  ref={newCourseThumbnailInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    setNewCourse({ ...newCourse, thumbnail: e.target.files?.[0] || null })
                  }
                  className={coreFieldClasses.fileInput}
                />
              </div>
              <div>
                <label className={coreFieldClasses.label}>ویدیو معرفی دوره</label>
                <input
                  type="file"
                  accept="video/*"
                  onChange={(e) =>
                    setNewCourse({ ...newCourse, video: e.target.files?.[0] || null })
                  }
                  className={coreFieldClasses.fileInput}
                />
              </div>
              <div>
                <label className={coreFieldClasses.label}>لینک اشتراک آپارات (ویدیو معرفی)</label>
                <p className="text-xs text-slate-500 mb-1.5">
                  اگر پر باشد، در سایت به‌صورت پلیر آپارات نمایش داده می‌شود؛ می‌توانید به‌جای یا همراه با فایل آپلودی استفاده کنید.
                </p>
                <input
                  type="url"
                  dir="ltr"
                  placeholder="https://www.aparat.com/v/…"
                  value={newCourse.introVideoAparatUrl}
                  onChange={(e) =>
                    setNewCourse({ ...newCourse, introVideoAparatUrl: e.target.value })
                  }
                  className={coreFieldClasses.input}
                />
              </div>
              <div>
                <label className={coreFieldClasses.label}>ضمیمه‌های عمومی دوره</label>
                <p className="text-xs text-slate-500 mb-1.5">
                  چند فایل؛ PDF، Word، متن آرشیوی، فشرده مجاز است.
                </p>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.txt,.zip,.rar"
                  onChange={(e) =>
                    setNewCourse({ ...newCourse, attachments: Array.from(e.target.files || []) })
                  }
                  className={coreFieldClasses.fileInput}
                />
              </div>
            </CoreSection>

            <CoreSection
              title="اپیزودهای ویدیو (به‌صورت لیست پویا)"
              subtitle={`حداکثر ${MAX_EPISODES_PER_TYPE} ویدیو؛ برای تقسیم‌بندی رسمی دوره بعد از ذخیره از تب «سرفصل‌ها» استفاده کنید.`}
              accent="teal"
            >
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <label className="text-sm font-semibold text-slate-800">
                  لیست آپلود ویدیوی اپیزود
                </label>
                <button
                  type="button"
                  onClick={addVideoField}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold text-white bg-[#1C3B9B] rounded-xl hover:bg-[#153088] transition-colors shadow-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  افزودن ردیف ویدیو
                </button>
              </div>
              <div className="space-y-3 max-h-72 overflow-y-auto pr-0.5">
                {newCourse.courseVideos.length === 0 ? (
                  <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/80 py-10 text-center">
                    <svg className="w-14 h-14 mx-auto text-slate-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <p className="text-sm font-medium text-slate-600">هنوز اپیزودی اضافه نشده است</p>
                    <p className="text-xs text-slate-400 mt-1">با «افزودن ردیف ویدیو» شروع کنید</p>
                  </div>
                ) : (
                  newCourse.courseVideos.map((video, index) => (
                    <div
                      key={video.id}
                      className="flex items-start gap-3 p-4 rounded-xl border border-blue-100 bg-gradient-to-br from-blue-50/90 to-white"
                    >
                      <div className="flex-shrink-0 w-10 h-10 bg-[#1C3B9B] text-white rounded-xl flex items-center justify-center text-sm font-bold shadow-sm">
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0 space-y-2">
                        <input
                          type="text"
                          placeholder="عنوان ویدیو (مثلاً: اپیزود ۱ − مقدمه)"
                          value={video.title}
                          onChange={(e) => updateVideoField(video.id, 'title', e.target.value)}
                          className={`${coreFieldClasses.input} text-sm py-2`}
                        />
                        <input
                          type="file"
                          accept="video/*"
                          onChange={(e) =>
                            updateVideoField(video.id, 'file', e.target.files?.[0] || null)
                          }
                          className={coreFieldClasses.fileInput}
                        />
                        {video.file && (
                          <p className="text-xs text-emerald-600 font-medium">
                            آماده آپلود: {video.file.name}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeVideoField(video.id)}
                        className="flex-shrink-0 text-red-600 hover:text-red-800 hover:bg-red-50 p-2 rounded-lg transition-colors"
                        title="حذف ردیف"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  ))
                )}
              </div>
            </CoreSection>

            <CoreSection
              title="اپیزودهای صوتی (به‌صورت لیست پویا)"
              subtitle={`برای هر ردیف، عنوان و یک فایل صوتی؛ حداکثر ${MAX_EPISODES_PER_TYPE} فایل روی هر دوره فعلی است.`}
              accent="teal"
            >
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <label className="text-sm font-semibold text-slate-800">
                  لیست آپلود فایل صوتی
                </label>
                <button
                  type="button"
                  onClick={addAudioField}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold text-white bg-violet-600 rounded-xl hover:bg-violet-700 transition-colors shadow-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  افزودن ردیف صوتی
                </button>
              </div>
              <div className="space-y-3 max-h-72 overflow-y-auto pr-0.5">
                {newCourse.courseAudios.length === 0 ? (
                  <div className="rounded-2xl border-2 border-dashed border-violet-100 bg-violet-50/40 py-10 text-center">
                    <svg className="w-14 h-14 mx-auto text-violet-300 mb-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"/>
                    </svg>
                    <p className="text-sm font-medium text-slate-600">ردیفی برای صوت ثبت نشده</p>
                    <p className="text-xs text-slate-400 mt-1">با «افزودن ردیف صوتی» پیش بروید</p>
                  </div>
                ) : (
                  newCourse.courseAudios.map((audio, index) => (
                    <div
                      key={audio.id}
                      className="flex items-start gap-3 p-4 rounded-xl border border-violet-100 bg-gradient-to-br from-violet-50/80 to-white"
                    >
                      <div className="flex-shrink-0 w-10 h-10 bg-violet-600 text-white rounded-xl flex items-center justify-center text-sm font-bold shadow-sm">
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0 space-y-2">
                        <input
                          type="text"
                          placeholder="عنوان (مثلاً: جلسه ۱ − مقدمه)"
                          value={audio.title}
                          onChange={(e) => updateAudioField(audio.id, 'title', e.target.value)}
                          className={`${coreFieldClasses.input} text-sm py-2`}
                        />
                        <input
                          type="file"
                          accept="audio/*"
                          onChange={(e) =>
                            updateAudioField(audio.id, 'file', e.target.files?.[0] || null)
                          }
                          className={coreFieldClasses.fileInput}
                        />
                        {audio.file && (
                          <p className="text-xs text-emerald-600 font-medium">
                            آماده آپلود: {audio.file.name}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAudioField(audio.id)}
                        className="flex-shrink-0 text-red-600 hover:text-red-800 hover:bg-red-50 p-2 rounded-lg transition-colors"
                        title="حذف ردیف"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  ))
                )}
              </div>
            </CoreSection>

            <p className="text-xs text-center text-slate-500 pb-1">
              پس از «ایجاد دوره»، سرفصل و جلسهٔ رسمی را از «سرفصل‌ها» یا از ویرایش همان دوره باز کنید.
            </p>
          </div>
          )}

          {newCourseModalTab === 'about' && (
            <CourseAboutPageFields
              longDescription={newCourse.longDescription}
              onLongDescriptionChange={(html) => setNewCourse({ ...newCourse, longDescription: html })}
              learningOutcomes={newCourse.learningOutcomes}
              onLearningOutcomesChange={(rows) => setNewCourse({ ...newCourse, learningOutcomes: rows })}
              summaryBullets={newCourse.summaryBullets}
              onSummaryBulletsChange={(rows) => setNewCourse({ ...newCourse, summaryBullets: rows })}
              characteristics={newCourse.courseCharacteristics}
              onCharacteristicsChange={(rows) => setNewCourse({ ...newCourse, courseCharacteristics: rows })}
            />
          )}

          {newCourseModalTab === 'prerequisites' && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-4 space-y-4">
              <p className="text-sm font-bold text-amber-950 border-b border-amber-200 pb-2">پیش‌نیازهای این دوره</p>
              <p className="text-xs text-amber-900/90 leading-relaxed">
                برای هر ردیف یک دورهٔ دیگر از لیست انتخاب کنید و مشخص کنید برای مخاطب «پیشنهادی» است یا «ضروری». دورهٔ در حال
                ساخت را نمی‌توان به عنوان پیش‌نیاز انتخاب کرد تا پس از ذخیره، در ویرایش دوره اضافه شود.
              </p>
              {newPrerequisites.length === 0 && (
                <p className="text-xs text-slate-600">هنوز پیش‌نیازی ثبت نشده — با دکمه زیر اضافه کنید.</p>
              )}
              <div className="space-y-3">
                {newPrerequisites.map((row, idx) => {
                  const selectedElsewhere = new Set(
                    newPrerequisites
                      .filter((_, i) => i !== idx)
                      .map((r) => r.prerequisiteCourseId)
                      .filter(Boolean),
                  );
                  const options = courses.filter((c) => !selectedElsewhere.has(c.id) || c.id === row.prerequisiteCourseId);
                  return (
                    <div
                      key={`new-pre-${idx}`}
                      className="flex flex-col gap-2 rounded-lg border border-amber-100 bg-white p-3 sm:flex-row sm:items-end sm:flex-wrap"
                    >
                      <div className="min-w-[200px] flex-1">
                        <label className="block text-xs font-medium text-gray-600 mb-1">دورهٔ پیش‌نیاز</label>
                        <select
                          value={row.prerequisiteCourseId}
                          onChange={(e) => {
                            const next = [...newPrerequisites];
                            next[idx] = { ...next[idx], prerequisiteCourseId: e.target.value };
                            setNewPrerequisites(next);
                          }}
                          className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          <option value="">انتخاب کنید…</option>
                          {options.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.title}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="w-full sm:w-44">
                        <label className="block text-xs font-medium text-gray-600 mb-1">نوع</label>
                        <select
                          value={row.requirementType}
                          onChange={(e) => {
                            const next = [...newPrerequisites];
                            next[idx] = {
                              ...next[idx],
                              requirementType: e.target.value as 'SUGGESTED' | 'MANDATORY',
                            };
                            setNewPrerequisites(next);
                          }}
                          className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          <option value="SUGGESTED">پیشنهادی</option>
                          <option value="MANDATORY">ضروری</option>
                        </select>
                      </div>
                      <button
                        type="button"
                        className="text-sm text-red-600 px-2 py-2 self-end"
                        onClick={() => setNewPrerequisites(newPrerequisites.filter((_, i) => i !== idx))}
                      >
                        حذف
                      </button>
                    </div>
                  );
                })}
              </div>
              <button
                type="button"
                className="text-sm font-medium text-amber-800 hover:text-amber-950"
                onClick={() =>
                  setNewPrerequisites([
                    ...newPrerequisites,
                    { prerequisiteCourseId: '', requirementType: 'SUGGESTED' },
                  ])
                }
              >
                + افزودن پیش‌نیاز
              </button>
            </div>
          )}

          {newCourseModalTab === 'curriculum' && (
            <div className="rounded-xl border border-teal-200 bg-gradient-to-br from-teal-50/90 to-white p-6 space-y-4 text-right">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 border-b border-teal-100 pb-4">
                <div>
                  <p className="text-sm font-bold text-teal-950">سرفصل‌ها و محتوای جلسات</p>
                  <p className="text-xs text-teal-900/85 mt-1 leading-relaxed max-w-xl">
                    در این مرحله هنوز شناسهٔ دوره در سرور وجود ندارد؛ بنابراین فصل و جلسه و اتصال ویدیو/صوت بعد از ذخیرهٔ دوره
                    فعال می‌شوند — دقیقاً مثل پیش‌نیازها که تا قبل از ذخیره دورهٔ جدید کامل نیستند.
                  </p>
                </div>
                <div className="shrink-0 flex h-12 w-12 items-center justify-center rounded-lg bg-teal-100 text-teal-700">
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.75}
                      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13M12 6.253"
                    />
                  </svg>
                </div>
              </div>
              <ol className="list-decimal list-inside space-y-2 text-sm text-slate-800 pr-1">
                <li>این فرم را در تب «اطلاعات پایه» تکمیل کنید و دوره را با «ایجاد دوره» ذخیره کنید.</li>
                <li>در جدول دوره‌ها برای همان دوره روی «سرفصل‌ها» کلیک کنید؛ یا «ویرایش» را بزنید و تب «سرفصل‌ها» را انتخاب کنید.</li>
                <li>فصل‌ها و جلسات را بسازید و برای هر جلسه ویدیو یا صوت همان دوره را وصل کنید و در صورت نیاز منتشر کنید.</li>
                <li>
                  برای <span className="font-semibold">تمرین‌های عملی و جزوهٔ PDF هر جلسه</span>، پس از ذخیره همان دوره را ویرایش
                  کنید و تب «تمرین‌ها و جزوات» را باز کنید (یا از تب راهنما در همین پنجره جزئیات را ببینید).
                </li>
              </ol>
              <button
                type="button"
                onClick={() => setNewCourseModalTab('core')}
                className="text-sm font-medium text-teal-800 hover:text-teal-950 underline-offset-4 hover:underline"
              >
                بازگشت به تب «اطلاعات پایه» برای ذخیره
              </button>
            </div>
          )}

          {newCourseModalTab === 'practiceSessions' && (
            <div className="rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50/90 to-white p-6 space-y-4 text-right">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 border-b border-violet-100 pb-4">
                <div>
                  <p className="text-sm font-bold text-violet-950">تمرین‌ها و جزوات برای دورهٔ جدید</p>
                  <p className="text-xs text-violet-900/85 mt-1 leading-relaxed max-w-xl">
                    جلسات تمرین روی سرور به شناسهٔ دوره وصل می‌شوند؛ قبل از کلیک روی «ایجاد دوره» هنوز دوره در دیتابیس ساخته
                    نشده، بنابراین همین‌جا فرمی برای افزودن تمرین نمی‌بینید — اول دوره را ذخیره کنید، بعد در ویرایش دوره تنظیم
                    می‌شود.
                  </p>
                </div>
                <div className="shrink-0 flex h-12 w-12 items-center justify-center rounded-lg bg-violet-100 text-violet-700">
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.75}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
              </div>
              <ol className="list-decimal list-inside space-y-2 text-sm text-slate-800 pr-1">
                <li>در تب «اطلاعات پایه» دکمهٔ «ایجاد دوره» را بزنید تا دوره در سیستم ثبت شود.</li>
                <li>از جدول دوره‌ها برای همان ردیف، «ویرایش دوره» را باز کنید.</li>
                <li>
                  تب <span className="font-semibold">«تمرین‌ها و جزوات»</span> را انتخاب کنید؛ برای هر جلسه می‌توانید عنوان، ترتیب،
                  وضعیت انتشار، فایل PDF جزوه، و چک‌لیست تمرین (متن آیتم‌ها) را تعریف یا ویرایش کنید.
                </li>
              </ol>
              <p className="text-xs text-slate-600 leading-relaxed border-t border-violet-100 pt-4">
                نکته: تمرین‌ها به‌صورت خودکار با «سرفصل و جلسه» در LMS یکی نیستند؛ عنوان هر جلسهٔ تمرین را می‌توانید شبیه جلسهٔ
                اصلی دوره نام‌گذاری کنید تا برای دانشجو واضح باشد.
              </p>
              <button
                type="button"
                onClick={() => setNewCourseModalTab('core')}
                className="text-sm font-medium text-violet-800 hover:text-violet-950 underline-offset-4 hover:underline"
              >
                بازگشت به تب «اطلاعات پایه» برای ذخیرهٔ دوره
              </button>
            </div>
          )}

          <div className="flex justify-end space-x-2 space-x-reverse pt-4">
            <button
              type="button"
              onClick={() => {
                setIsModalOpen(false);
                setNewCourseModalTab('core');
                setNewPrerequisites([]);
              }}
              disabled={isUploading}
              className={`px-4 py-2 text-sm font-medium border rounded-lg transition-colors ${
                isUploading 
                  ? 'text-gray-400 bg-gray-50 border-gray-200 cursor-not-allowed' 
                  : 'text-gray-700 bg-gray-100 border-gray-300 hover:bg-gray-200'
              }`}
            >
              انصراف
            </button>
            <button
              type="submit"
              disabled={isUploading}
              className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-lg transition-colors ${
                isUploading 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {isUploading ? 'در حال آپلود...' : 'ایجاد دوره'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingCourse(null);
          setCourseVideos([]);
          setCourseAudios([]);
          setNewVideos([]);
          setNewAudios([]);
          setEditAttachments([]);
          setNewAttachmentFiles([]);
          setNewThumbnailFile(null);
          setNewIntroVideoFile(null);
          setEditCourseModalTab('core');
          setEditPrerequisites([]);
        }}
        title="ویرایش دوره"
        size="wide"
      >
        {isUploading && (
          <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-blue-900">در حال آپلود فایل‌ها...</span>
              <span className="text-sm font-semibold text-blue-700">{Math.round(uploadProgress)}%</span>
            </div>
            {currentUploadFile && (
              <p className="text-xs text-blue-700 mb-2 truncate" title={currentUploadFile}>
                📁 {currentUploadFile}
              </p>
            )}
            <ProgressBar progress={uploadProgress} className="mb-2" />
            {uploadedBytes > 0 && totalBytes > 0 && (
              <div className="flex justify-between text-xs text-blue-600">
                <span>{(uploadedBytes / (1024 * 1024)).toFixed(2)} MB</span>
                <span>از {(totalBytes / (1024 * 1024)).toFixed(2)} MB</span>
              </div>
            )}
          </div>
        )}
        <div className="space-y-6">
          <div className="-mx-4 sm:-mx-6 flex flex-wrap gap-0 border-b border-slate-200/90 bg-[#fafafa] px-2 sm:px-3 mb-1">
            <button
              type="button"
              onClick={() => setEditCourseModalTab('core')}
              className={`relative flex-1 min-h-[48px] min-w-[120px] px-2 sm:px-3 py-3 text-sm font-bold transition-colors ${
                editCourseModalTab === 'core'
                  ? 'text-[#1C3B9B] after:absolute after:bottom-0 after:left-3 after:right-3 after:h-[3px] after:rounded-t after:bg-[#1C3B9B]'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              اطلاعات پایه و فایل‌ها
            </button>
            <button
              type="button"
              onClick={() => setEditCourseModalTab('about')}
              className={`relative flex-1 min-h-[48px] min-w-[120px] px-2 sm:px-3 py-3 text-sm font-bold transition-colors ${
                editCourseModalTab === 'about'
                  ? 'text-[#1C3B9B] after:absolute after:bottom-0 after:left-3 after:right-3 after:h-[3px] after:rounded-t after:bg-[#1C3B9B]'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              محتوای «درباره دوره» (سایت)
            </button>
            <button
              type="button"
              onClick={() => setEditCourseModalTab('prerequisites')}
              className={`relative flex-1 min-h-[48px] min-w-[120px] px-2 sm:px-3 py-3 text-sm font-bold transition-colors ${
                editCourseModalTab === 'prerequisites'
                  ? 'text-[#1C3B9B] after:absolute after:bottom-0 after:left-3 after:right-3 after:h-[3px] after:rounded-t after:bg-[#1C3B9B]'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              پیش‌نیازها
            </button>
            <button
              type="button"
              onClick={() => setEditCourseModalTab('curriculum')}
              className={`relative flex-1 min-h-[48px] min-w-[120px] px-2 sm:px-3 py-3 text-sm font-bold transition-colors ${
                editCourseModalTab === 'curriculum'
                  ? 'text-[#1C3B9B] after:absolute after:bottom-0 after:left-3 after:right-3 after:h-[3px] after:rounded-t after:bg-[#1C3B9B]'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              سرفصل‌ها
            </button>
            <button
              type="button"
              onClick={() => setEditCourseModalTab('lessonUpdates')}
              className={`relative flex-1 min-h-[48px] min-w-[120px] px-2 sm:px-3 py-3 text-sm font-bold transition-colors ${
                editCourseModalTab === 'lessonUpdates'
                  ? 'text-[#1C3B9B] after:absolute after:bottom-0 after:left-3 after:right-3 after:h-[3px] after:rounded-t after:bg-[#1C3B9B]'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              آپدیت جلسات
            </button>
            <button
              type="button"
              onClick={() => setEditCourseModalTab('practiceSessions')}
              className={`relative flex-1 min-h-[48px] min-w-[120px] px-2 sm:px-3 py-3 text-sm font-bold transition-colors ${
                editCourseModalTab === 'practiceSessions'
                  ? 'text-[#1C3B9B] after:absolute after:bottom-0 after:left-3 after:right-3 after:h-[3px] after:rounded-t after:bg-[#1C3B9B]'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              تمرین‌ها و جزوات
            </button>
            <button
              type="button"
              onClick={() => setEditCourseModalTab('faq')}
              className={`relative flex-1 min-h-[48px] min-w-[120px] px-2 sm:px-3 py-3 text-sm font-bold transition-colors ${
                editCourseModalTab === 'faq'
                  ? 'text-[#1C3B9B] after:absolute after:bottom-0 after:left-3 after:right-3 after:h-[3px] after:rounded-t after:bg-[#1C3B9B]'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              سوالات متداول
            </button>
            <button
              type="button"
              onClick={() => setEditCourseModalTab('satisfaction')}
              className={`relative flex-1 min-h-[48px] min-w-[120px] px-2 sm:px-3 py-3 text-sm font-bold transition-colors ${
                editCourseModalTab === 'satisfaction'
                  ? 'text-[#1C3B9B] after:absolute after:bottom-0 after:left-3 after:right-3 after:h-[3px] after:rounded-t after:bg-[#1C3B9B]'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              رضایت کاربران
            </button>
          </div>

          {editCourseModalTab === 'core' && (
          <div className="space-y-5">
            <CoreFormRibbon mode="edit" />

            <CoreSection
              title="عنوان و معرفی کوتاه"
              subtitle="همان خلاصه‌ای که در کارت‌ها و لیست دوره‌ها دیده می‌شود؛ متن بلند در تب «درباره دوره»."
              accent="brand"
            >
              <div>
                <label className={coreFieldClasses.label}>عنوان دوره</label>
                <input
                  type="text"
                  value={editingCourse?.title || ''}
                  onChange={(e) =>
                    setEditingCourse((prev) => (prev ? { ...prev, title: e.target.value } : null))
                  }
                  className={coreFieldClasses.input}
                  required
                />
              </div>
              <div>
                <label className={coreFieldClasses.label}>توضیحات کوتاه</label>
                <textarea
                  value={editingCourse?.description || ''}
                  onChange={(e) =>
                    setEditingCourse((prev) =>
                      prev ? { ...prev, description: e.target.value } : null,
                    )
                  }
                  className={coreFieldClasses.textarea}
                  rows={3}
                  maxLength={2000}
                />
                <p className={coreFieldClasses.counter}>
                  {(editingCourse?.description?.length || 0)}/۲۰۰۰ کاراکتر
                </p>
              </div>
            </CoreSection>

            <CoreSection
              title="فروش و ویژگی‌های سریع"
              subtitle="قیمت و دسته؛ فیلدهای زیر مستقیماً در دیتابیس دوره ذخیره می‌شوند و کنار «کارت‌ها» استفاده می‌شوند."
              accent="violet"
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={coreFieldClasses.label}>دسته‌بندی محتوا</label>
                  <select
                    value={editingCourse?.categoryId || ''}
                    onChange={(e) =>
                      setEditingCourse((prev) =>
                        prev ? { ...prev, categoryId: e.target.value || null } : null,
                      )
                    }
                    className={coreFieldClasses.select}
                  >
                    <option value="">بدون دسته‌بندی</option>
                    {courseCategories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.path || category.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {editingCourse ? (
                <CourseCoreFreeCourseToggle
                  idPrefix="edit-course"
                  isFreeCourse={Boolean(editingCourse.isFreeCourse)}
                  onChange={(nextFree) =>
                    setEditingCourse((prev) =>
                      prev
                        ? nextFree
                          ? {
                              ...prev,
                              isFreeCourse: true,
                              price: 0,
                              discountType: 'NONE',
                              discountPercent: 0,
                              discountAmount: 0,
                              discountStartsAt: null,
                              discountEndsAt: null,
                              featuredBestsellerCarousel: false,
                            }
                          : { ...prev, isFreeCourse: false }
                        : null,
                    )
                  }
                />
              ) : null}
              {editingCourse && (
                <CoursePricingAdminBlock
                  locked={Boolean(editingCourse.isFreeCourse)}
                  values={{
                    listPrice: editingCourse.price,
                    discountType: editingCourse.discountType ?? 'NONE',
                    discountPercent: editingCourse.discountPercent ?? 0,
                    discountAmount: editingCourse.discountAmount ?? 0,
                    discountStartsAt: editingCourse.discountStartsAt ?? null,
                    discountEndsAt: editingCourse.discountEndsAt ?? null,
                  }}
                  onChange={(patch) =>
                    setEditingCourse((prev) => {
                      if (!prev) return null;
                      const next = { ...prev, ...patch };
                      if (patch.listPrice !== undefined) {
                        next.price = patch.listPrice;
                      }
                      return next;
                    })
                  }
                />
              )}
              <CourseCoreQuickSpecFields
                values={{
                  courseLevel: editingCourse?.courseLevel ?? '',
                  suggestedDailyTime: editingCourse?.suggestedDailyTime ?? '',
                  teachingFormat: editingCourse?.teachingFormat ?? '',
                  finalOutcomeLabel: editingCourse?.finalOutcomeLabel ?? '',
                }}
                onChange={(next) =>
                  setEditingCourse((prev) => (prev ? { ...prev, ...next } : null))
                }
              />
            </CoreSection>

            <CoreSection
              title="انتشار، صفحه اصلی و نظرات"
              accent="amber"
              subtitle="فروشگاه و بلوک‌های خانه؛ کاروسل «رایگان» با تیک «دوره رایگان» در بخش فروش ست می‌شود."
            >
              <CourseCorePublishToggles
                idPrefix="edit-course"
                published={Boolean(editingCourse?.published)}
                showOnHomepage={editingCourse?.showOnHomepage ?? true}
                allowComments={editingCourse?.allowComments ?? true}
                onChange={(field, value) =>
                  setEditingCourse((prev) => (prev ? { ...prev, [field]: value } : null))
                }
              />
              <div className="border-t border-slate-100 pt-4">
                <p className="mb-3 text-xs font-bold text-slate-800">کاروسل‌های صفحهٔ اصلی (غیر از رایگان)</p>
                <p className="mb-3 text-[11px] leading-relaxed text-slate-500">
                  جدیدترین، پرطرفدار و پرفروش را اینجا فعال می‌کنید؛ حضور در کاروسل رایگان با تیک «این دوره رایگان است» انجام می‌شود.
                </p>
                {editingCourse ? (
                  <CourseCoreHomepageCarouselToggles
                    idPrefix="edit-course-carousel"
                    includeBestsellerCarousel={!editingCourse.isFreeCourse}
                    featuredPopularCarousel={Boolean(editingCourse.featuredPopularCarousel)}
                    featuredBestsellerCarousel={Boolean(editingCourse.featuredBestsellerCarousel)}
                    featuredNewestCarousel={Boolean(editingCourse.featuredNewestCarousel)}
                    onChange={(field, value) =>
                      setEditingCourse((prev) => (prev ? { ...prev, [field]: value } : null))
                    }
                  />
                ) : null}
              </div>
            </CoreSection>

            <CoreSection
              title="فایل‌های اصلی رسانه"
              subtitle={`تصویر شاخص، ویدیو معرفی و ضمایم عمومی؛ ${editAttachments.length} فایل ضمیمهٔ فعلی روی سرور.`}
              accent="slate"
            >
              <div>
                <label className={coreFieldClasses.label}>تصویر شاخص</label>
                <p className="mb-2 rounded-lg border border-sky-100 bg-sky-50/80 px-3 py-2 text-[11px] leading-relaxed text-slate-700">
                  <span className="font-bold text-sky-900">راهنمای ابعاد (فرانت): </span>
                  {COURSE_THUMBNAIL_HINT_FA}
                </p>
                {editNewThumbnailPreviewUrl || editingCourse?.thumbnail ? (
                  <div className="mb-3 flex flex-wrap items-start gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <img
                      src={editNewThumbnailPreviewUrl || editingCourse?.thumbnail || ''}
                      alt={editingCourse?.title || 'تصویر شاخص'}
                      className="h-36 w-36 rounded-xl border border-white object-cover shadow-md sm:h-40 sm:w-40"
                      crossOrigin={editNewThumbnailPreviewUrl ? undefined : 'anonymous'}
                    />
                    <div className="min-w-0 flex-1 space-y-2">
                      {editNewThumbnailPreviewUrl ? (
                        <>
                          <p className="text-sm font-semibold text-slate-800">پیش‌نمایش تصویر جدید</p>
                          <p className="truncate text-xs text-slate-600" title={newThumbnailFile?.name}>
                            {newThumbnailFile?.name}
                          </p>
                          <p className="text-xs text-amber-700">
                            تا زمان کلیک بر «به‌روزرسانی دوره» و اتمام آپلود، تصویر قبلی روی سرور باقی است.
                          </p>
                          <button
                            type="button"
                            onClick={() => {
                              setNewThumbnailFile(null);
                              if (editCourseThumbnailInputRef.current)
                                editCourseThumbnailInputRef.current.value = '';
                            }}
                            className="rounded-xl border border-red-200 bg-white px-3 py-1.5 text-xs font-bold text-red-600 hover:bg-red-50"
                          >
                            حذف تصویر انتخاب‌شده
                          </button>
                        </>
                      ) : editingCourse?.thumbnail ? (
                        <>
                          <p className="text-sm font-semibold text-slate-800">تصویر شاخص فعلی (روی سرور)</p>
                          <a
                            href={editingCourse.thumbnail}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex text-xs font-semibold text-[#1C3B9B] hover:underline"
                          >
                            باز کردن در تب جدید
                          </a>
                          <p className="text-xs text-slate-500">
                            برای تعویض، فایل جدید انتخاب کنید؛ پیش‌نمایش جایگزین این بخش می‌شود.
                          </p>
                        </>
                      ) : null}
                    </div>
                  </div>
                ) : (
                  <p className="mb-2 text-xs text-slate-500">هنوز تصویر ثبت نشده است؛ پس از انتخاب فایل پیش‌نمایش اینجاست.</p>
                )}
                <input
                  ref={editCourseThumbnailInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => setNewThumbnailFile(e.target.files?.[0] || null)}
                  className={coreFieldClasses.fileInput}
                />
              </div>

              <div>
                <label className={coreFieldClasses.label}>ویدیو معرفی دوره</label>
                {editingCourse?.videoFile ? (
                  <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 mb-2 text-xs text-slate-600">
                    <span className="truncate">{editingCourse.videoFile}</span>
                    <a
                      href={editingCourse.videoFile}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-[#1C3B9B] shrink-0"
                    >
                      مشاهده
                    </a>
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 mb-2">ویدیوی معرفی ثبت نشده است.</p>
                )}
                <input
                  type="file"
                  accept="video/*"
                  onChange={(e) => setNewIntroVideoFile(e.target.files?.[0] || null)}
                  className={coreFieldClasses.fileInput}
                />
                {newIntroVideoFile && (
                  <div className="mt-2 flex items-center justify-between rounded-xl px-3 py-2 text-xs font-medium text-blue-800 bg-blue-50 border border-blue-100">
                    <span className="truncate">{newIntroVideoFile.name}</span>
                    <button
                      type="button"
                      onClick={() => setNewIntroVideoFile(null)}
                      className="text-red-600 hover:text-red-800 shrink-0"
                    >
                      حذف انتخاب
                    </button>
                  </div>
                )}
              </div>

              <div>
                <label className={coreFieldClasses.label}>لینک اشتراک آپارات (ویدیو معرفی)</label>
                <p className="text-xs text-slate-500 mb-1.5">
                  اولویت نمایش در سایت با embed آپارات؛ در صورت خالی بودن، از فایل آپلودی استریم می‌شود.
                </p>
                <input
                  type="url"
                  dir="ltr"
                  placeholder="https://www.aparat.com/v/…"
                  value={editingCourse?.introVideoAparatUrl ?? ''}
                  onChange={(e) =>
                    setEditingCourse((prev) =>
                      prev ? { ...prev, introVideoAparatUrl: e.target.value } : null,
                    )
                  }
                  className={coreFieldClasses.input}
                />
              </div>

              <div className="pt-2 border-t border-slate-100">
                <div className="flex flex-wrap justify-between gap-2 mb-3">
                  <label className={coreFieldClasses.label}>ضمیمه‌های دوره</label>
                  <span className="text-xs font-medium text-slate-400">
                    {editAttachments.length} فایل روی سرور
                  </span>
                </div>
                {editAttachments.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto pr-0.5 mb-3">
                    {editAttachments.map((attachment) => (
                      <div
                        key={attachment.id}
                        className="flex items-center justify-between gap-2 rounded-xl px-3 py-2 bg-slate-50 border border-slate-100"
                      >
                        <a
                          href={attachment.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-medium text-[#1C3B9B] hover:underline truncate min-w-0"
                        >
                          {attachment.fileName}
                        </a>
                        <button
                          type="button"
                          onClick={() => handleRemoveAttachment(attachment.id)}
                          className="text-xs font-semibold text-red-600 hover:text-red-800 shrink-0"
                        >
                          حذف از لیست
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 mb-3">ضمیمه‌ای ثبت نشده است.</p>
                )}
                <label className="text-xs font-semibold text-slate-600 mb-1.5 block">
                  آپلود فایل ضمیمهٔ جدید
                </label>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.txt,.zip,.rar"
                  onChange={(e) => {
                    handleAttachmentSelection(e.target.files);
                    if (e.target) e.target.value = '';
                  }}
                  className={coreFieldClasses.fileInput}
                />
                {newAttachmentFiles.length > 0 && (
                  <div className="mt-2 space-y-2 max-h-40 overflow-y-auto pr-0.5">
                    {newAttachmentFiles.map((file, index) => (
                      <div
                        key={`${file.name}-${index}`}
                        className="flex items-center justify-between rounded-xl px-3 py-2 text-xs font-medium text-blue-800 bg-blue-50 border border-blue-100"
                      >
                        <span className="truncate">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveNewAttachment(index)}
                          className="text-red-600 hover:text-red-800 shrink-0"
                        >
                          حذف
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CoreSection>

          {courseVideos.length > 0 && (
            <CoreSection title={`اپیزودهای ویدیو (${courseVideos.length})`} accent="teal" subtitle="ویرایش متادیتا و ترتیب؛ ذخیرهٔ هر کارت جدا با دکمهٔ ذخیره است.">
              <div className="space-y-3 max-h-80 overflow-y-auto pr-0.5">
                {courseVideos.map((video) => (
                  <div
                    key={video.id}
                    className="p-4 rounded-xl border border-slate-100 bg-gradient-to-br from-slate-50/90 to-white space-y-3 shadow-sm"
                  >
                    <div className="grid gap-3 md:grid-cols-2">
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">عنوان</label>
                        <input
                          type="text"
                          value={video.title || ''}
                          onChange={(e) => handleVideoFieldChange(video.id, 'title', e.target.value)}
                          className={`${coreFieldClasses.input} text-sm py-2`}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">ترتیب</label>
                          <input
                            type="number"
                            value={video.order ?? 0}
                            onChange={(e) => handleVideoFieldChange(video.id, 'order', Number(e.target.value))}
                            className={`${coreFieldClasses.input} text-sm py-2`}
                            min={0}
                          />
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={video.published}
                            onChange={(e) => handleVideoFieldChange(video.id, 'published', e.target.checked)}
                            className="h-4 w-4 text-[#1C3B9B] focus:ring-[#1C3B9B] border-gray-300 rounded"
                          />
                          <span className="mr-2 text-sm text-gray-700">منتشر شده</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">توضیحات</label>
                      <textarea
                        value={video.description || ''}
                        onChange={(e) => handleVideoFieldChange(video.id, 'description', e.target.value)}
                        className={`${coreFieldClasses.textarea} text-sm py-2 min-h-[3.5rem]`}
                        rows={2}
                      />
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t border-slate-100">
                      {video.videoFile ? (
                        <a
                          href={video.videoFile}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-semibold text-[#1C3B9B] hover:underline"
                        >
                          مشاهده ویدیو
                        </a>
                      ) : (
                        <span>فایل ویدیو بارگذاری نشده است.</span>
                      )}
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => handleSaveVideoChanges(video)}
                          className="px-3 py-1.5 text-xs font-bold text-white bg-[#1C3B9B] rounded-xl hover:bg-[#153088] transition-colors"
                        >
                          ذخیره تغییرات
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteVideo(video.id)}
                          className="px-3 py-1.5 text-xs font-bold text-white bg-red-600 rounded-xl hover:bg-red-700 transition-colors"
                        >
                          حذف
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CoreSection>
          )}

          <CoreSection
            title="افزودن ویدیوهای جدید"
            accent="teal"
            subtitle={`با ذخیرهٔ فرم آپلود انجام می‌شود؛ سقف ${MAX_EPISODES_PER_TYPE} ویدیو روی هر دوره.`}
          >
            <div className="flex flex-wrap justify-between items-center gap-2 mb-1">
              <span className="text-sm font-semibold text-slate-800">
                پیش‌نویس ردیف‌های جدید
              </span>
              <button
                type="button"
                onClick={addNewVideoField}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 transition-colors shadow-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                افزودن ردیف
              </button>
            </div>
            {newVideos.length === 0 ? (
              <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/80 py-10 text-center">
                <svg className="w-14 h-14 mx-auto text-slate-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <p className="text-sm font-medium text-slate-600">برای آپلود، حداقل یک ردیف اضافه کنید.</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-72 overflow-y-auto pr-0.5">
                {newVideos.map((video, index) => (
                  <div key={video.id} className="flex items-start gap-3 p-4 rounded-xl border border-blue-100 bg-gradient-to-br from-blue-50/90 to-white">
                    <div className="flex-shrink-0 w-10 h-10 bg-[#1C3B9B] text-white rounded-xl flex items-center justify-center text-sm font-bold shadow-sm">
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0 space-y-2">
                      <input
                        type="text"
                        placeholder="عنوان ویدیو (مثلاً: اپیزود ۱ − مقدمه)"
                        value={video.title}
                        onChange={(e) => updateNewVideoField(video.id, 'title', e.target.value)}
                        className={`${coreFieldClasses.input} text-sm py-2`}
                      />
                      <input
                        type="file"
                        accept="video/*"
                        onChange={(e) => updateNewVideoField(video.id, 'file', e.target.files?.[0] || null)}
                        className={coreFieldClasses.fileInput}
                      />
                      {video.file && (
                        <p className="text-xs font-medium text-emerald-600">
                          انتخاب شد: {video.file.name} ({(video.file.size / (1024 * 1024)).toFixed(2)} MB)
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeNewVideoField(video.id)}
                      className="flex-shrink-0 text-red-600 hover:bg-red-50 p-2 rounded-lg transition-colors"
                      title="حذف"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </CoreSection>

          {courseAudios.length > 0 && (
            <CoreSection title={`فایل‌های صوتی ثبت‌شده (${courseAudios.length})`} accent="teal" subtitle="هر کارت؛ ذخیره جدا قبل از ترک این صفحه توصیه می‌شود.">
              <div className="space-y-3 max-h-80 overflow-y-auto pr-0.5">
                {courseAudios.map((audio) => (
                  <div
                    key={audio.id}
                    className="p-4 rounded-xl border border-violet-100 bg-gradient-to-br from-violet-50/80 to-white space-y-3 shadow-sm"
                  >
                    <div className="grid gap-3 md:grid-cols-2">
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">عنوان</label>
                        <input
                          type="text"
                          value={audio.title || ''}
                          onChange={(e) => handleAudioFieldChange(audio.id, 'title', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus-border-transparent text-sm"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">ترتیب</label>
                          <input
                            type="number"
                            value={audio.order ?? 0}
                            onChange={(e) => handleAudioFieldChange(audio.id, 'order', Number(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus-border-transparent text-sm"
                            min={0}
                          />
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={audio.published}
                            onChange={(e) => handleAudioFieldChange(audio.id, 'published', e.target.checked)}
                            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                          />
                          <span className="mr-2 text-sm text-gray-700">منتشر شده</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">توضیحات</label>
                      <textarea
                        value={audio.description || ''}
                        onChange={(e) => handleAudioFieldChange(audio.id, 'description', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus-border-transparent text-sm"
                        rows={2}
                      />
                    </div>
                    <div className="flex items-end gap-2">
                      <div className="flex-1">
                        <label className="text-xs text-gray-500 mb-1 block">مدت زمان (ثانیه)</label>
                        <input
                          type="number"
                          value={audio.duration ?? 0}
                          onChange={(e) => handleAudioFieldChange(audio.id, 'duration', Number(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                          min={0}
                          placeholder="مثلاً ۳۶۰ برای ۶ دقیقه"
                        />
                        {(audio.duration ?? 0) > 0 && (
                          <span className="text-xs text-teal-600 mt-0.5 block">
                            ≈ {Math.floor((audio.duration ?? 0) / 60)}:{String((audio.duration ?? 0) % 60).padStart(2, '0')} دقیقه
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleAutoDetectDuration(audio)}
                        className="px-3 py-2 text-xs font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded-lg hover:bg-teal-100 transition-colors whitespace-nowrap"
                      >
                        تشخیص خودکار
                      </button>
                    </div>
                    <div className="space-y-2">
                      {audio.audioFile ? (
                        <AudioPlayerComponent
                          audioUrl={
                            audio.audioFile.startsWith('http')
                              ? audio.audioFile
                              : `${API_ORIGIN}/uploads/${audio.audioFile}`
                          }
                        />
                      ) : (
                        <span className="text-xs text-gray-500">فایل صوتی بارگذاری نشده است.</span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleSaveAudioChanges(audio)}
                        className="px-3 py-1 text-xs font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
                      >
                        ذخیره تغییرات
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteAudio(audio.id)}
                        className="px-3 py-1 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                      >
                        حذف
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </CoreSection>
          )}

          <CoreSection
            title="افزودن فایل‌های صوتی جدید"
            accent="teal"
            subtitle={`همان فرم؛ پس از «ذخیره تغییرات دوره» همراه بقیه فایل‌ها ارسال می‌شود؛ سقف ${MAX_EPISODES_PER_TYPE} فایل صوتی.`}
          >
            <div className="flex flex-wrap justify-between gap-2 mb-1">
              <span className="text-sm font-semibold text-slate-800">پیش‌نویس ردیف‌ها</span>
              <button
                type="button"
                onClick={addNewAudioField}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold text-white bg-violet-600 rounded-xl hover:bg-violet-700 transition-colors shadow-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                افزودن ردیف صوت
              </button>
            </div>
            {newAudios.length === 0 ? (
              <div className="rounded-2xl border-2 border-dashed border-violet-100 bg-violet-50/40 py-10 text-center">
                <svg className="w-14 h-14 mx-auto text-violet-300 mb-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"/>
                </svg>
                <p className="text-sm font-medium text-slate-600">حداقل یک ردیف برای آپلود اضافه کنید.</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-72 overflow-y-auto pr-0.5">
                {newAudios.map((audio, index) => (
                  <div key={audio.id} className="flex items-start gap-3 p-4 rounded-xl border border-violet-100 bg-gradient-to-br from-violet-50/80 to-white">
                    <div className="flex-shrink-0 w-10 h-10 bg-violet-600 text-white rounded-xl flex items-center justify-center text-sm font-bold shadow-sm">
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0 space-y-2">
                      <input
                        type="text"
                        placeholder="عنوان صوت (مثلاً: جلسه ۱ − مقدمه)"
                        value={audio.title}
                        onChange={(e) => updateNewAudioField(audio.id, 'title', e.target.value)}
                        className={`${coreFieldClasses.input} text-sm py-2`}
                      />
                      <input
                        type="file"
                        accept="audio/*"
                        onChange={(e) => updateNewAudioField(audio.id, 'file', e.target.files?.[0] || null)}
                        className={coreFieldClasses.fileInput}
                      />
                      {audio.file && (
                        <p className="text-xs font-medium text-emerald-600">
                          انتخاب شد: {audio.file.name} ({(audio.file.size / (1024 * 1024)).toFixed(2)} MB)
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeNewAudioField(audio.id)}
                      className="flex-shrink-0 text-red-600 hover:bg-red-50 p-2 rounded-lg transition-colors"
                      title="حذف"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </CoreSection>

          </div>
          )}

          {editCourseModalTab === 'about' && editingCourse && (
            <CourseAboutPageFields
              longDescription={editingCourse.longDescription ?? ''}
              onLongDescriptionChange={(html) =>
                setEditingCourse((prev) => (prev ? { ...prev, longDescription: html } : null))
              }
              learningOutcomes={editingCourse.learningOutcomes ?? []}
              onLearningOutcomesChange={(rows) =>
                setEditingCourse((prev) => (prev ? { ...prev, learningOutcomes: rows } : null))
              }
              summaryBullets={editingCourse.summaryBullets ?? []}
              onSummaryBulletsChange={(rows) =>
                setEditingCourse((prev) => (prev ? { ...prev, summaryBullets: rows } : null))
              }
              characteristics={editingCourse.courseCharacteristics ?? []}
              onCharacteristicsChange={(rows) =>
                setEditingCourse((prev) => (prev ? { ...prev, courseCharacteristics: rows } : null))
              }
            />
          )}

          {editCourseModalTab === 'prerequisites' && editingCourse && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-4 space-y-4">
              <p className="text-sm font-bold text-amber-950 border-b border-amber-200 pb-2">پیش‌نیازهای این دوره</p>
              <p className="text-xs text-amber-900/90 leading-relaxed">
                دوره‌های دیگر را به عنوان پیش‌نیاز انتخاب کنید. این دوره ({editingCourse.title}) در لیست دوره‌های پیش‌نیاز
                نمایش داده نمی‌شود.
              </p>
              {editPrerequisites.length === 0 && (
                <p className="text-xs text-slate-600">هنوز پیش‌نیازی ثبت نشده — با دکمه زیر اضافه کنید.</p>
              )}
              <div className="space-y-3">
                {editPrerequisites.map((row, idx) => {
                  const selectedElsewhere = new Set(
                    editPrerequisites
                      .filter((_, i) => i !== idx)
                      .map((r) => r.prerequisiteCourseId)
                      .filter(Boolean),
                  );
                  const options = courses.filter(
                    (c) =>
                      c.id !== editingCourse.id &&
                      (!selectedElsewhere.has(c.id) || c.id === row.prerequisiteCourseId),
                  );
                  return (
                    <div
                      key={`edit-pre-${idx}`}
                      className="flex flex-col gap-2 rounded-lg border border-amber-100 bg-white p-3 sm:flex-row sm:items-end sm:flex-wrap"
                    >
                      <div className="min-w-[200px] flex-1">
                        <label className="block text-xs font-medium text-gray-600 mb-1">دورهٔ پیش‌نیاز</label>
                        <select
                          value={row.prerequisiteCourseId}
                          onChange={(e) => {
                            const next = [...editPrerequisites];
                            next[idx] = { ...next[idx], prerequisiteCourseId: e.target.value };
                            setEditPrerequisites(next);
                          }}
                          className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          <option value="">انتخاب کنید…</option>
                          {options.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.title}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="w-full sm:w-44">
                        <label className="block text-xs font-medium text-gray-600 mb-1">نوع</label>
                        <select
                          value={row.requirementType}
                          onChange={(e) => {
                            const next = [...editPrerequisites];
                            next[idx] = {
                              ...next[idx],
                              requirementType: e.target.value as 'SUGGESTED' | 'MANDATORY',
                            };
                            setEditPrerequisites(next);
                          }}
                          className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          <option value="SUGGESTED">پیشنهادی</option>
                          <option value="MANDATORY">ضروری</option>
                        </select>
                      </div>
                      <button
                        type="button"
                        className="text-sm text-red-600 px-2 py-2 self-end"
                        onClick={() => setEditPrerequisites(editPrerequisites.filter((_, i) => i !== idx))}
                      >
                        حذف
                      </button>
                    </div>
                  );
                })}
              </div>
              <button
                type="button"
                className="text-sm font-medium text-amber-800 hover:text-amber-950"
                onClick={() =>
                  setEditPrerequisites([
                    ...editPrerequisites,
                    { prerequisiteCourseId: '', requirementType: 'SUGGESTED' },
                  ])
                }
              >
                + افزودن پیش‌نیاز
              </button>
            </div>
          )}

          {editCourseModalTab === 'curriculum' && editingCourse && (
            <CourseCurriculumTab courseId={editingCourse.id} videos={courseVideos} audios={courseAudios} />
          )}

          {editCourseModalTab === 'lessonUpdates' && editingCourse && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                این بخش <span className="font-semibold text-slate-800">فقط دستی</span> است: هر آپدیت را خودتان اضافه می‌کنید؛ از
                لیست جلسات جایی خودکار پر نمی‌شود. هر ردیف با دکمهٔ ثبت همین فرم ذخیره می‌شود. موارد با وضعیت «منتشر شده» در
                تب «آپدیت جلسات» سایت نشان داده می‌شوند.
              </p>
              <CourseLessonUpdatesTab courseId={editingCourse.id} />
            </div>
          )}

          {editCourseModalTab === 'practiceSessions' && editingCourse && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                جلسات تمرین، چک‌لیست و جزوه PDF هر جلسه را اینجا مدیریت می‌کنید. فقط دوره‌های دارای ثبت‌نام می‌توانند
                پیشرفت و بازخورد را روی سرور ذخیره کنند.
              </p>
              <CoursePracticeSessionsTab courseId={editingCourse.id} />
            </div>
          )}

          {editCourseModalTab === 'faq' && editingCourse && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                سوال و جواب هر مورد را اینجا بسازید. دسته همان گروه‌بندیِ تب «سوالات متداول» در سایت است؛ فقط موارد با
                وضعیت «منتشر شده» برای بازدیدکنندگان نمایش داده می‌شوند.
              </p>
              <CourseFaqsTab courseId={editingCourse.id} />
            </div>
          )}

          {editCourseModalTab === 'satisfaction' && editingCourse && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                سکشن «رضایت کاربران» در صفحهٔ عمومی دوره از اینجا پر می‌شود: میانگین و کارت‌های آماری؛ برای هر شرکت‌کننده
                نام، شغل، ستاره، متن کارت، تصویر و لینک ویدیوی آپارات. فقط موارد «منتشر شده» روی سایت نمایش داده
                می‌شوند.
              </p>
              <CourseSatisfactionTab
                courseId={editingCourse.id}
                satisfactionAvgLabel={editingCourse.satisfactionAvgLabel}
                satisfactionKpis={editingCourse.satisfactionKpis}
                onHeaderSynced={(patch) =>
                  setEditingCourse((prev) => (prev ? { ...prev, ...patch } : null))
                }
              />
            </div>
          )}

          <div className="flex justify-end space-x-2 space-x-reverse pt-4">
            <button
              type="button"
              onClick={() => {
                setIsEditModalOpen(false);
                setEditingCourse(null);
                setEditCourseModalTab('core');
                setEditPrerequisites([]);
              }}
              className={`px-4 py-2 text-sm font-medium border rounded-lg transition-colors ${
                isUploading
                  ? 'text-gray-400 bg-gray-50 border-gray-200 cursor-not-allowed'
                  : 'text-gray-700 bg-gray-100 border-gray-300 hover:bg-gray-200'
              }`}
              disabled={isUploading}
            >
              انصراف
            </button>
            <button
              type="button"
              onClick={(e) => void handleUpdateCourse(e)}
              className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-lg transition-colors ${
                isUploading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
              disabled={isUploading}
            >
              {isUploading ? 'در حال ویرایش...' : 'ویرایش دوره'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Users Management Modal */}
      <Modal
        isOpen={isUsersModalOpen}
        onClose={() => {
          setIsUsersModalOpen(false);
          setSelectedCourse(null);
          setEnrollments([]);
          setSelectedUserIds(new Set());
          setTargetCourseId('');
        }}
        title={`مدیریت کاربران دوره: ${selectedCourse?.title || ''}`}
        size="wide"
      >
        {loadingEnrollments ? (
          <div className="flex justify-center py-8">
            <SiteBrandedLoader
              variant="section"
              message="بارگذاری ثبت‌نام‌ها…"
              className="min-h-0 bg-transparent py-0"
            />
          </div>
        ) : (
          <div className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedUserIds.size === validEnrollments.length && validEnrollments.length > 0}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">انتخاب همه ({validEnrollments.length} کاربر)</span>
                </label>
              </div>
              <div className="text-sm text-gray-600">
                {selectedUserIds.size > 0 && `${selectedUserIds.size} کاربر انتخاب شده`}
              </div>
            </div>

            {validEnrollments.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                هیچ کاربری در این دوره ثبت‌نام نکرده است
              </div>
            ) : (
              <>
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          انتخاب
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          نام کاربری
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          نام
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          ایمیل
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          تلفن
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          تاریخ ثبت‌نام
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {paginatedEnrollments.map((enrollment) => (
                          <tr key={enrollment.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={selectedUserIds.has(enrollment.userId)}
                                onChange={() => handleToggleUser(enrollment.userId)}
                                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                              />
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {enrollment.user?.username || '-'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {enrollment.user?.firstName || ''} {enrollment.user?.lastName || ''}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                              {enrollment.user?.email || '-'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                              {enrollment.user?.phone || '-'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                              {enrollment.enrolledAt ? new Date(enrollment.enrolledAt).toLocaleDateString('fa-IR') : '-'}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                {validEnrollments.length > 0 && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                      <span>
                        نمایش {paginatedEnrollments.length} کاربر (صفحه {enrollmentsPage} از {totalEnrollmentPages})
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setEnrollmentsPage((p) => Math.max(1, p - 1))}
                          disabled={enrollmentsPage === 1}
                          className="px-3 py-1 rounded border text-sm disabled:opacity-50 bg-white hover:bg-gray-100"
                        >
                          قبلی
                        </button>
                        <button
                          onClick={() => setEnrollmentsPage((p) => Math.min(totalEnrollmentPages, p + 1))}
                          disabled={enrollmentsPage === totalEnrollmentPages}
                          className="px-3 py-1 rounded border text-sm disabled:opacity-50 bg-white hover:bg-gray-100"
                        >
                          بعدی
                        </button>
                      </div>
                    </div>

                    <div className="border-t pt-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        انتقال همه کاربران به دوره:
                      </label>
                      <select
                        value={targetCourseId}
                        onChange={(e) => setTargetCourseId(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">انتخاب دوره مقصد</option>
                        {courses
                          .filter(course => course.id !== selectedCourse?.id)
                          .map(course => (
                            <option key={course.id} value={course.id}>
                              {course.title}
                            </option>
                          ))}
                      </select>
                    </div>
                    <button
                      onClick={handleTransferUsers}
                      disabled={!targetCourseId || transferring}
                      className="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {transferring ? (
                        <>
                          <BrandSpinnerRing inverse size="sm" />
                          <span>در حال انتقال...</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                          </svg>
                          <span>انتقال همه {validEnrollments.length} کاربر به دوره انتخاب شده</span>
                        </>
                      )}
                    </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Courses;